import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import { useAppSelector } from '../../hooks/hooks';
import { useUploadDocument } from '../../hooks/useUploadDocument';
import { supabase } from '../../lib/supabase';
import type { UserOrganisation } from '../../types/types';
import { AddProductStepCircularity } from './AddProductStepCircularity';
import { AddProductStepCompliance } from './AddProductStepCompliance';
import {
  buildDppCompletenessChecks,
  calculateDppCompletenessScore,
} from './dppCompleteness';
import { AddProductStepIdentity } from './AddProductStepIdentity';
import type {
  CareSymbolCategory,
  CategoryCode,
  CeResponsibleEntityType,
  EchaCandidateSubstance,
  ExistingCircularityRecord,
  ExistingDeclarationRecord,
  ExistingPassportRecord,
  ExistingProductRecord,
  MachineClassification,
  Material,
  PackagingItem,
  ProductForm,
  ProductMetadata,
  Step,
  SupplyChainTraceabilityRow,
  SvhcDeclarationRow,
} from './productFormTypes';
import { productCategoryOptions } from './productFormTypes';

type StepValidationErrors = Partial<Record<string, string>>;
const GTIN_CHECK_DEBOUNCE_MS = 450;
const SKU_CHECK_DEBOUNCE_MS = 450;

// ─── ISO 3758 Care Symbol Picker data ───────────────────────────────────────

const X = (
  <line
    x1="5"
    y1="5"
    x2="35"
    y2="35"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  />
);

const tubFrame = (
  <>
    <rect
      x="5"
      y="13"
      width="30"
      height="21"
      rx="2"
      stroke="currentColor"
      strokeWidth="2.5"
    />
    <line
      x1="5"
      y1="13"
      x2="3"
      y2="8"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <line
      x1="35"
      y1="13"
      x2="37"
      y2="8"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </>
);

const svgWrap = (children: React.ReactNode) => (
  <svg
    viewBox="0 0 40 40"
    width="28"
    height="28"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const careSymbolCategories: CareSymbolCategory[] = [
  {
    category: 'Washing',
    options: [
      {
        value: 'Do Not Wash',
        label: 'Do Not Wash',
        icon: svgWrap(
          <>
            {tubFrame}
            {X}
          </>,
        ),
      },
      {
        value: 'Hand Wash',
        label: 'Hand Wash',
        icon: svgWrap(
          <>
            {tubFrame}
            <rect
              x="15"
              y="22"
              width="10"
              height="7"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <line
              x1="17"
              y1="22"
              x2="17"
              y2="18"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <line
              x1="20"
              y1="22"
              x2="20"
              y2="17"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <line
              x1="23"
              y1="22"
              x2="23"
              y2="18"
              stroke="currentColor"
              strokeWidth="1.8"
            />
          </>,
        ),
      },
      {
        value: 'Machine Wash Cold (30°C)',
        label: 'Machine Wash Cold (30°C)',
        icon: svgWrap(
          <>
            {tubFrame}
            <text
              x="20"
              y="28"
              textAnchor="middle"
              fontSize="10"
              fontWeight="bold"
              fontFamily="system-ui,sans-serif"
              fill="currentColor"
            >
              30
            </text>
          </>,
        ),
      },
      {
        value: 'Machine Wash Warm (40°C)',
        label: 'Machine Wash Warm (40°C)',
        icon: svgWrap(
          <>
            {tubFrame}
            <text
              x="20"
              y="28"
              textAnchor="middle"
              fontSize="10"
              fontWeight="bold"
              fontFamily="system-ui,sans-serif"
              fill="currentColor"
            >
              40
            </text>
          </>,
        ),
      },
      {
        value: 'Machine Wash Hot (60°C+)',
        label: 'Machine Wash Hot (60°C+)',
        icon: svgWrap(
          <>
            {tubFrame}
            <text
              x="20"
              y="28"
              textAnchor="middle"
              fontSize="10"
              fontWeight="bold"
              fontFamily="system-ui,sans-serif"
              fill="currentColor"
            >
              60
            </text>
          </>,
        ),
      },
    ],
  },
  {
    category: 'Bleaching',
    options: [
      {
        value: 'Do Not Bleach',
        label: 'Do Not Bleach',
        icon: svgWrap(
          <>
            <path
              d="M20,5 L36,34 L4,34 Z"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            {X}
          </>,
        ),
      },
      {
        value: 'Bleach Allowed',
        label: 'Bleach Allowed (Any)',
        icon: svgWrap(
          <path
            d="M20,5 L36,34 L4,34 Z"
            stroke="currentColor"
            strokeWidth="2.5"
          />,
        ),
      },
      {
        value: 'Non-Chlorine Bleach Only',
        label: 'Non-Chlorine Bleach Only',
        icon: svgWrap(
          <>
            <path
              d="M20,5 L36,34 L4,34 Z"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <line
              x1="15"
              y1="20"
              x2="22"
              y2="32"
              stroke="currentColor"
              strokeWidth="2"
            />
            <line
              x1="19"
              y1="20"
              x2="26"
              y2="32"
              stroke="currentColor"
              strokeWidth="2"
            />
          </>,
        ),
      },
    ],
  },
  {
    category: 'Drying',
    options: [
      {
        value: 'Do Not Tumble Dry',
        label: 'Do Not Tumble Dry',
        icon: svgWrap(
          <>
            <rect
              x="4"
              y="4"
              width="32"
              height="32"
              rx="2"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle
              cx="20"
              cy="20"
              r="11"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            {X}
          </>,
        ),
      },
      {
        value: 'Tumble Dry Low',
        label: 'Tumble Dry Low Heat',
        icon: svgWrap(
          <>
            <rect
              x="4"
              y="4"
              width="32"
              height="32"
              rx="2"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle
              cx="20"
              cy="20"
              r="11"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle cx="20" cy="20" r="2" fill="currentColor" />
          </>,
        ),
      },
      {
        value: 'Tumble Dry Normal',
        label: 'Tumble Dry Normal Heat',
        icon: svgWrap(
          <>
            <rect
              x="4"
              y="4"
              width="32"
              height="32"
              rx="2"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle
              cx="20"
              cy="20"
              r="11"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle cx="15" cy="20" r="2" fill="currentColor" />
            <circle cx="25" cy="20" r="2" fill="currentColor" />
          </>,
        ),
      },
      {
        value: 'Line Dry / Hang to Dry',
        label: 'Line Dry / Hang to Dry',
        icon: svgWrap(
          <>
            <rect
              x="4"
              y="4"
              width="32"
              height="32"
              rx="2"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <line
              x1="9"
              y1="20"
              x2="31"
              y2="20"
              stroke="currentColor"
              strokeWidth="2.5"
            />
          </>,
        ),
      },
    ],
  },
  {
    category: 'Ironing',
    options: [
      {
        value: 'Do Not Iron',
        label: 'Do Not Iron',
        icon: svgWrap(
          <>
            <path
              d="M4,16 L4,28 L30,28 Q38,28 38,22 Q38,16 30,16 Z"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <path
              d="M10,16 L10,11 L22,11 L22,16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            {X}
          </>,
        ),
      },
      {
        value: 'Iron Low (110°C)',
        label: 'Iron Low (110°C)',
        icon: svgWrap(
          <>
            <path
              d="M4,16 L4,28 L30,28 Q38,28 38,22 Q38,16 30,16 Z"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <path
              d="M10,16 L10,11 L22,11 L22,16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle cx="20" cy="22" r="2" fill="currentColor" />
          </>,
        ),
      },
      {
        value: 'Iron Medium (150°C)',
        label: 'Iron Medium (150°C)',
        icon: svgWrap(
          <>
            <path
              d="M4,16 L4,28 L30,28 Q38,28 38,22 Q38,16 30,16 Z"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <path
              d="M10,16 L10,11 L22,11 L22,16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle cx="16" cy="22" r="2" fill="currentColor" />
            <circle cx="24" cy="22" r="2" fill="currentColor" />
          </>,
        ),
      },
      {
        value: 'Iron High (200°C)',
        label: 'Iron High (200°C)',
        icon: svgWrap(
          <>
            <path
              d="M4,16 L4,28 L30,28 Q38,28 38,22 Q38,16 30,16 Z"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <path
              d="M10,16 L10,11 L22,11 L22,16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle cx="13" cy="22" r="2" fill="currentColor" />
            <circle cx="20" cy="22" r="2" fill="currentColor" />
            <circle cx="27" cy="22" r="2" fill="currentColor" />
          </>,
        ),
      },
    ],
  },
  {
    category: 'Professional Care',
    options: [
      {
        value: 'Do Not Dry Clean',
        label: 'Do Not Dry Clean',
        icon: svgWrap(
          <>
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            {X}
          </>,
        ),
      },
      {
        value: 'Dry Clean (P)',
        label: 'Dry Clean – Perchloroethylene (P)',
        icon: svgWrap(
          <>
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <text
              x="20"
              y="20"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="15"
              fontWeight="bold"
              fontFamily="system-ui,sans-serif"
              fill="currentColor"
            >
              P
            </text>
          </>,
        ),
      },
      {
        value: 'Dry Clean (F)',
        label: 'Dry Clean – Hydrocarbon Solvents (F)',
        icon: svgWrap(
          <>
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <text
              x="20"
              y="20"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="15"
              fontWeight="bold"
              fontFamily="system-ui,sans-serif"
              fill="currentColor"
            >
              F
            </text>
          </>,
        ),
      },
      {
        value: 'Wet Clean (W)',
        label: 'Wet Clean (W)',
        icon: svgWrap(
          <>
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <text
              x="20"
              y="20"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="15"
              fontWeight="bold"
              fontFamily="system-ui,sans-serif"
              fill="currentColor"
            >
              W
            </text>
          </>,
        ),
      },
    ],
  },
];

const categoryOptions: Array<{ code: CategoryCode; name: string }> =
  productCategoryOptions;

const MACHINERY_DECLARATION_MAX_BYTES = 10 * 1024 * 1024;

const initialForm = (organisationName?: string): ProductForm => ({
  name: '',
  productSummary: '',
  productWeight: '',
  productWeightUnit: 'g',
  skuModel: '',
  gtinEan: '',
  brandName: organisationName ?? '',
  categoryCode: '',
  manufacturerName: organisationName ?? '',
  manufacturerAddress: '',
  importedOutsideEu: false,
  importerName: '',
  importerEmail: '',
  importerAddress: '',
  importerEori: '',
  seasonCollection: '',
  targetAudience: [],
  hardwareRevision: '',
  machineClassification: '',
  ceResponsiblePerson: '',
  declarationOfConformityFile: null,
  declarationOfIncorporationFile: null,
  notifiedBodyNumber: '',
  notifiedBodyCertNumber: '',
  notifiedBodyCertificateFile: null,
  ceResponsibleEntityType: '',
  ceResponsibleCompanyName: '',
  ceResponsibleEuAddress: '',
  ceResponsibleContactEmail: '',
  materials: [
    {
      material_name: '',
      weight_percentage: '',
      recycled_content_percentage: '0',
      is_recyclable: false,
    },
  ],
  svhcStatus: '',
  svhcDeclarations: [],
  containsElectricalComponents: false,
  rohsStatus: '',
  rohsExemptionCode: '',
  customExemptionDetails: '',
  rohsExclusionReason: '',
  rohsSupportingDocumentFile: null,
  dopDocumentFile: null,
  vocEmissionClass: '',
  eudrDdsReference: '',
  flameRetardantsPresent: false,
  flameRetardantChemicalNames: [],
  ufiCode: '',
  clpHazardPhrases: [],
  criticalRawMaterials: false,
  criticalRawMaterialsList: [],
  weeeRegistrationNumber: '',
  includeSupplyChainTraceability: false,
  supplyChainTraceability: [],
  popsPfasStatus: '',
  popsPfasTestReportFile: null,
  popsPfasExemptionDetails: '',
  includeConsumerPackaging: true,
  packagingItems: [],
  packagingEprRegistration: '',
  documentIds: [],
  carbonFootprint: '',
  lifespan: '',
  lifespanUnit: 'years',
  repairabilityScore: '',
  repairManualFile: null,
  disassemblyGuideFile: null,
  demolitionGuideFile: null,
  demolitionGuideUrl: '',
  recyclingInstructions: '',
  ratedCapacityAh: '',
  ratedEnergyKwh: '',
  nominalVoltageV: '',
  minimumVoltageV: '',
  maximumVoltageV: '',
  powerCapabilityLimits: '',
  roundTripEfficiencyPercent: '',
  cycleLifetimeDescriptor: '',
  operatingTemperatureWindow: '',
  extinguishingAgents: '',
  safetyHandlingMeasures: '',
  restrictTechnicalSpecs: false,
  careSymbols: [],
  colorFastnessRating: '',
  pillingResistanceRating: '',
  waterConsumption: '',
  hasRemovableCovers: false,
  productionRoute: '',
  cbamEmbeddedEmissions: '',
  rollingResistanceClass: '',
  wetGripClass: '',
  abrasionRateMgKm: '',
  isRetreadable: false,
  surfactantBiodegradabilityPercentage: '',
  sparePartsAvailability: '',
  sparePartsAvailabilityYears: '',
  sparePartsCatalogUrl: '',
  takeBackScheme: '',
  microplasticsStatus: '',
  microplasticsReportFile: null,
  eprRegistrationNumber: '',
  softwareSupportYears: '',
  softwareSupportExpiry: '',
  energyEfficiencyRating: '',
});

const isValidGtin = (value: string) => {
  if (!/^\d{13,14}$/.test(value)) return false;

  const digits = value.split('').map(Number);
  const checkDigit = digits.pop();
  const sum = digits
    .reverse()
    .reduce(
      (total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1),
      0,
    );

  return (10 - (sum % 10)) % 10 === checkDigit;
};

const disallowedElectronicsRecyclingInstructionsPattern =
  /\b(?:regular bin|trash|rubbish)\b/i;

const requiredWeeeCollectionPattern =
  /\b(?:weee|e-waste|electronic waste|separate collection|collection point|take-back|take back|recycling cent(?:re|er)|crossed-out wheeled bin)\b/i;

const getElectronicsRecyclingInstructionsError = (
  recyclingInstructions: string,
) => {
  const normalizedInstructions = recyclingInstructions.trim();

  if (!normalizedInstructions) {
    return 'WEEE recycling instructions are required for electronics products.';
  }

  if (
    disallowedElectronicsRecyclingInstructionsPattern.test(
      normalizedInstructions,
    )
  ) {
    return 'Electronics recycling instructions must not direct disposal to a regular bin, trash, or rubbish.';
  }

  if (!requiredWeeeCollectionPattern.test(normalizedInstructions)) {
    return 'Electronics recycling instructions must include WEEE or separate collection guidance.';
  }

  return null;
};

const getBatteryTechnicalDataErrors = (form: ProductForm) => {
  const fieldErrors: StepValidationErrors = {};

  if (!form.ratedCapacityAh.trim()) {
    fieldErrors.ratedCapacityAh =
      'Rated capacity (Ah) is required for battery passports.';
  }

  if (!form.ratedEnergyKwh.trim()) {
    fieldErrors.ratedEnergyKwh =
      'Rated energy (kWh) is required for battery passports.';
  }

  if (!form.nominalVoltageV.trim()) {
    fieldErrors.nominalVoltageV =
      'Nominal voltage is required for battery passports.';
  }

  if (!form.minimumVoltageV.trim()) {
    fieldErrors.minimumVoltageV =
      'Minimum voltage is required for battery passports.';
  }

  if (!form.maximumVoltageV.trim()) {
    fieldErrors.maximumVoltageV =
      'Maximum voltage is required for battery passports.';
  }

  if (!form.powerCapabilityLimits.trim()) {
    fieldErrors.powerCapabilityLimits =
      'C-rate / power capability limits are required for battery passports.';
  }

  if (!form.roundTripEfficiencyPercent.trim()) {
    fieldErrors.roundTripEfficiencyPercent =
      'Round-trip energy efficiency is required for battery passports.';
  }

  if (!form.cycleLifetimeDescriptor.trim()) {
    fieldErrors.cycleLifetimeDescriptor =
      'Expected cycle lifetime is required for battery passports.';
  }

  if (!form.operatingTemperatureWindow.trim()) {
    fieldErrors.operatingTemperatureWindow =
      'Operating temperature window is required for battery passports.';
  }

  if (!form.extinguishingAgents.trim()) {
    fieldErrors.extinguishingAgents =
      'Recommended extinguishing agents are required for battery passports.';
  }

  if (!form.safetyHandlingMeasures.trim()) {
    fieldErrors.safetyHandlingMeasures =
      'Safety handling measures are required for battery passports.';
  }

  return fieldErrors;
};

const containsWoodOrLeatherMaterials = (materials: Material[]) =>
  materials.some((material) =>
    /\b(wood|timber|oak|pine|beech|birch|plywood|veneer|leather|hide|suede)\b/i.test(
      material.material_name,
    ),
  );

const asOptionalNumber = (value: string) =>
  value.trim() === '' ? null : Number(value);

const toEditableString = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value)
    ? ''
    : String(value);

const getProductCategoryCode = (
  productCategories: ExistingProductRecord['product_categories'],
): CategoryCode | '' => {
  const category = Array.isArray(productCategories)
    ? productCategories[0]
    : productCategories;
  const code = category?.code;

  return code && categoryOptions.some((option) => option.code === code)
    ? (code as CategoryCode)
    : '';
};

const isStorageDocumentPath = (value: string | null | undefined) =>
  Boolean(value && !/^https?:\/\//i.test(value));

const isPdfFile = (file: File) => /\.pdf$/i.test(file.name);

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const isValidUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidEori = (value: string) => /^[A-Za-z]{2}[A-Za-z0-9]+$/.test(value);

const isPartlyCompletedMachinery = (
  value: ProductForm['machineClassification'] | MachineClassification | null,
) => value === 'PARTLY_COMPLETED' || value === 'PARTLY_COMPLETED_MACHINERY';

const normalizeMachineClassification = (
  value: string | null | undefined,
): MachineClassification | '' => {
  if (!value) {
    return '';
  }

  return isPartlyCompletedMachinery(value as MachineClassification)
    ? 'PARTLY_COMPLETED_MACHINERY'
    : (value as MachineClassification);
};

const getMachineryDeclarationField = (
  machineClassification: ProductForm['machineClassification'],
) =>
  isPartlyCompletedMachinery(machineClassification)
    ? 'declarationOfIncorporationFile'
    : 'declarationOfConformityFile';

const formatCeResponsibleSummary = (
  companyName: string | null,
  euAddress: string | null,
) => {
  if (companyName && euAddress) {
    return `${companyName}, ${euAddress}`;
  }

  return companyName ?? euAddress;
};

const getEffectiveCeResponsiblePerson = (form: ProductForm) => {
  const usesImporterAsResponsiblePerson =
    form.categoryCode === 'INDUSTRIAL_MACHINERY' &&
    form.importedOutsideEu &&
    form.ceResponsibleEntityType !== 'AUTHORIZED_REPRESENTATIVE';
  const usesAutoEuManufacturer =
    form.categoryCode === 'INDUSTRIAL_MACHINERY' &&
    !form.importedOutsideEu &&
    form.ceResponsibleEntityType !== 'AUTHORIZED_REPRESENTATIVE';

  if (usesImporterAsResponsiblePerson) {
    const companyName = form.importerName.trim() || null;
    const euAddress = form.importerAddress.trim() || null;

    return {
      entityType: 'EU_IMPORTER' as const,
      companyName,
      euAddress,
      contactEmail: form.importerEmail.trim() || null,
      summary: (() => {
        const fallbackSummary = form.ceResponsiblePerson.trim() || null;
        return (
          formatCeResponsibleSummary(companyName, euAddress) ?? fallbackSummary
        );
      })(),
    };
  }

  if (usesAutoEuManufacturer) {
    const companyName = form.manufacturerName.trim() || null;
    const euAddress = form.manufacturerAddress.trim() || null;

    return {
      entityType: 'EU_MANUFACTURER' as const,
      companyName,
      euAddress,
      contactEmail: null,
      summary: (() => {
        const fallbackSummary = form.ceResponsiblePerson.trim() || null;
        return (
          formatCeResponsibleSummary(companyName, euAddress) ?? fallbackSummary
        );
      })(),
    };
  }

  const companyName = form.ceResponsibleCompanyName.trim();
  const entityType = form.ceResponsibleEntityType || null;
  const euAddress = form.ceResponsibleEuAddress.trim() || null;

  return {
    entityType,
    companyName: companyName || null,
    euAddress,
    contactEmail: form.ceResponsibleContactEmail.trim() || null,
    summary: (() => {
      const fallbackSummary = form.ceResponsiblePerson.trim() || null;
      return (
        formatCeResponsibleSummary(companyName || null, euAddress) ??
        fallbackSummary
      );
    })(),
  };
};

function getFirstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

const buildProductMetadata = (
  form: ProductForm,
  options?: {
    declarationOfConformityDocumentId?: string | null;
    declarationOfIncorporationDocumentId?: string | null;
    notifiedBodyCertificateDocumentId?: string | null;
    dopDocumentPath?: string | null;
    demolitionGuideUrl?: string | null;
  },
): ProductMetadata => {
  const isIndustrial = form.categoryCode === 'INDUSTRIAL_MACHINERY';
  const isConstruction = form.categoryCode === 'CONSTRUCTION_PRODUCTS';
  const isFurniture = form.categoryCode === 'FURNITURE_MATTRESSES';
  const isIronSteel = form.categoryCode === 'IRON_STEEL_METALS';
  const isTiresAutomotive = form.categoryCode === 'TIRES_AUTOMOTIVE';
  const isChemicals = form.categoryCode === 'CHEMICALS';
  const isHighRiskMachinery =
    isIndustrial && form.machineClassification === 'HIGH_RISK_MACHINERY';
  const isPartlyCompleted = isPartlyCompletedMachinery(
    form.machineClassification,
  );
  const effectiveCeResponsiblePerson = getEffectiveCeResponsiblePerson(form);

  return {
    product_weight_value: asOptionalNumber(form.productWeight),
    product_weight_unit: form.productWeight.trim()
      ? form.productWeightUnit
      : null,
    manufacturer_name: form.manufacturerName.trim() || null,
    manufacturer_address: form.manufacturerAddress.trim() || null,
    imported_outside_eu: form.importedOutsideEu,
    importer_name: form.importerName.trim() || null,
    importer_email: form.importerEmail.trim() || null,
    importer_address: form.importerAddress.trim() || null,
    importer_eori: form.importerEori.trim() || null,
    season_collection: form.seasonCollection.trim() || null,
    target_audience:
      form.targetAudience.length > 0 ? form.targetAudience : null,
    hardware_revision: form.hardwareRevision.trim() || null,
    machine_classification: isIndustrial
      ? normalizeMachineClassification(form.machineClassification)
      : null,
    ce_responsible_person: isIndustrial
      ? effectiveCeResponsiblePerson.summary
      : null,
    declaration_of_conformity_document_id:
      isIndustrial && !isPartlyCompleted
        ? (options?.declarationOfConformityDocumentId ?? null)
        : null,
    declaration_of_incorporation_document_id:
      isIndustrial && isPartlyCompleted
        ? (options?.declarationOfIncorporationDocumentId ?? null)
        : null,
    ce_responsible_person_entity_type: isIndustrial
      ? effectiveCeResponsiblePerson.entityType
      : null,
    ce_responsible_person_company_name: isIndustrial
      ? effectiveCeResponsiblePerson.companyName
      : null,
    ce_responsible_person_eu_address: isIndustrial
      ? effectiveCeResponsiblePerson.euAddress
      : null,
    ce_responsible_person_contact_email: isIndustrial
      ? effectiveCeResponsiblePerson.contactEmail
      : null,
    contains_electrical_components: isIndustrial
      ? form.containsElectricalComponents
      : undefined,
    notified_body_number: isHighRiskMachinery
      ? form.notifiedBodyNumber.trim() || null
      : null,
    notified_body_cert_number: isHighRiskMachinery
      ? form.notifiedBodyCertNumber.trim() || null
      : null,
    notified_body_certificate_document_id: isHighRiskMachinery
      ? (options?.notifiedBodyCertificateDocumentId ?? null)
      : null,
    rohs_exemption_code: form.rohsExemptionCode.trim() || null,
    custom_exemption_details: form.customExemptionDetails.trim() || null,
    rohs_exclusion_reason: form.rohsExclusionReason.trim() || null,
    dop_document_path: isConstruction
      ? (options?.dopDocumentPath ?? null)
      : null,
    voc_emission_class: isConstruction ? form.vocEmissionClass || null : null,
    eudr_dds_reference: isFurniture
      ? form.eudrDdsReference.trim() || null
      : null,
    flame_retardants_present: isFurniture ? form.flameRetardantsPresent : null,
    flame_retardant_chemical_names:
      isFurniture &&
      form.flameRetardantsPresent &&
      form.flameRetardantChemicalNames.length > 0
        ? form.flameRetardantChemicalNames
        : null,
    ufi_code: isChemicals ? form.ufiCode.trim() || null : null,
    clp_hazard_phrases:
      isChemicals && form.clpHazardPhrases.length > 0
        ? form.clpHazardPhrases
        : null,
    critical_raw_materials: form.criticalRawMaterials,
    critical_raw_materials_list:
      form.criticalRawMaterials && form.criticalRawMaterialsList.length > 0
        ? form.criticalRawMaterialsList
        : null,
    weee_registration_number: form.weeeRegistrationNumber.trim() || null,
    include_supply_chain_traceability: form.includeSupplyChainTraceability,
    supply_chain_traceability:
      form.supplyChainTraceability.length > 0
        ? form.supplyChainTraceability
            .filter(
              (row) =>
                row.company_name.trim() ||
                row.country_code.trim() ||
                row.facility_id_or_address.trim() ||
                row.process_component.trim(),
            )
            .map((row) => ({
              tier: row.tier,
              company_name: row.company_name.trim(),
              country_code: row.country_code,
              facility_id_or_address: row.facility_id_or_address.trim(),
              process_component: row.process_component.trim(),
            }))
        : null,
    include_consumer_packaging: form.includeConsumerPackaging,
    lifespan_unit: form.lifespanUnit,
    demolition_guide_url: isConstruction
      ? (options?.demolitionGuideUrl ?? null)
      : null,
    has_removable_covers: isFurniture ? form.hasRemovableCovers : null,
    production_route: isIronSteel ? form.productionRoute || null : null,
    cbam_embedded_emissions: isIronSteel
      ? asOptionalNumber(form.cbamEmbeddedEmissions)
      : null,
    rolling_resistance_class: isTiresAutomotive
      ? form.rollingResistanceClass || null
      : null,
    wet_grip_class: isTiresAutomotive ? form.wetGripClass || null : null,
    abrasion_rate_mg_km: isTiresAutomotive
      ? asOptionalNumber(form.abrasionRateMgKm)
      : null,
    is_retreadable: isTiresAutomotive ? form.isRetreadable : null,
    surfactant_biodegradability_percentage: isChemicals
      ? asOptionalNumber(form.surfactantBiodegradabilityPercentage)
      : null,
    software_support_expiry: form.softwareSupportExpiry || null,
    energy_efficiency_rating: form.energyEfficiencyRating.trim() || null,
  };
};

const buildFormFromExistingData = ({
  product,
  materials,
  packaging,
  packagingEprRegistration,
  declarations,
  circularity,
  passport,
  organisationName,
}: {
  product: ExistingProductRecord;
  materials: Material[];
  packaging: PackagingItem[];
  packagingEprRegistration: string;
  declarations: ExistingDeclarationRecord[];
  circularity: ExistingCircularityRecord | null;
  passport: ExistingPassportRecord | null;
  organisationName?: string;
}): ProductForm => {
  const metadata = product.metadata ?? {};
  const reachDeclarations = declarations.filter(
    (declaration) => declaration.declaration_type === 'REACH_SVHC',
  );
  const rohsDeclaration = declarations.find(
    (declaration) => declaration.declaration_type === 'ROHS',
  );
  const popsPfasDeclaration = declarations.find(
    (declaration) => declaration.declaration_type === 'POPS_PFAS',
  );
  const ceDocumentIds = declarations
    .filter((declaration) => declaration.declaration_type === 'CE_DOC')
    .map((declaration) => declaration.document_id)
    .filter((value): value is string => Boolean(value));
  const notifiedBodyCertificateDocumentId =
    metadata.notified_body_certificate_document_id ?? null;
  const declarationOfConformityDocumentId =
    metadata.declaration_of_conformity_document_id ?? null;
  const declarationOfIncorporationDocumentId =
    metadata.declaration_of_incorporation_document_id ?? null;
  const reachDocumentIds = reachDeclarations
    .map((declaration) => declaration.document_id)
    .filter((value): value is string => Boolean(value));

  return {
    ...initialForm(organisationName),
    name: product.name,
    productSummary: product.description ?? '',
    productWeight: toEditableString(metadata.product_weight_value),
    productWeightUnit: metadata.product_weight_unit === 'kg' ? 'kg' : 'g',
    skuModel: product.sku_model,
    gtinEan: product.gtin_ean ?? '',
    brandName: product.brand_name,
    categoryCode: getProductCategoryCode(product.product_categories),
    manufacturerName: metadata.manufacturer_name ?? organisationName ?? '',
    manufacturerAddress: metadata.manufacturer_address ?? '',
    importedOutsideEu: Boolean(metadata.imported_outside_eu),
    importerName: metadata.importer_name ?? '',
    importerEmail: metadata.importer_email ?? '',
    importerAddress: metadata.importer_address ?? '',
    importerEori: metadata.importer_eori ?? '',
    seasonCollection: metadata.season_collection ?? '',
    targetAudience: Array.isArray(metadata.target_audience)
      ? metadata.target_audience
      : [],
    hardwareRevision: metadata.hardware_revision ?? '',
    machineClassification: normalizeMachineClassification(
      metadata.machine_classification,
    ),
    ceResponsiblePerson:
      metadata.ce_responsible_person_company_name ??
      metadata.ce_responsible_person ??
      '',
    declarationOfConformityFile: null,
    declarationOfIncorporationFile: null,
    notifiedBodyNumber: metadata.notified_body_number ?? '',
    notifiedBodyCertNumber: metadata.notified_body_cert_number ?? '',
    notifiedBodyCertificateFile: null,
    ceResponsibleEntityType:
      (metadata.ce_responsible_person_entity_type as CeResponsibleEntityType | null) ??
      '',
    ceResponsibleCompanyName:
      metadata.ce_responsible_person_company_name ??
      metadata.ce_responsible_person ??
      '',
    ceResponsibleEuAddress: metadata.ce_responsible_person_eu_address ?? '',
    ceResponsibleContactEmail:
      metadata.ce_responsible_person_contact_email ?? '',
    materials:
      materials.length > 0
        ? materials.map((material) => ({
            material_name: material.material_name,
            weight_percentage: material.weight_percentage,
            recycled_content_percentage: material.recycled_content_percentage,
            is_recyclable: material.is_recyclable,
          }))
        : initialForm(organisationName).materials,
    svhcStatus:
      reachDeclarations.length === 0
        ? ''
        : reachDeclarations.some(
              (declaration) =>
                declaration.svhc_present || declaration.substance_name?.trim(),
            )
          ? 'contains'
          : 'none',
    svhcDeclarations: reachDeclarations
      .filter((declaration) => declaration.substance_name?.trim())
      .map((declaration) => ({
        substance_name: declaration.substance_name ?? '',
        cas_ec_number: declaration.cas_ec_number ?? '',
        present_in_component: declaration.present_in_component ?? '',
        scip_dossier_number: declaration.scip_number ?? '',
      })),
    containsElectricalComponents:
      Boolean(metadata.contains_electrical_components) ||
      Boolean(rohsDeclaration),
    rohsStatus: rohsDeclaration
      ? rohsDeclaration.status === 'EXEMPT'
        ? 'not_applicable'
        : metadata.rohs_exemption_code
          ? 'with_exemption'
          : 'without_exemption'
      : '',
    rohsExemptionCode: metadata.rohs_exemption_code ?? '',
    customExemptionDetails: metadata.custom_exemption_details ?? '',
    rohsExclusionReason: metadata.rohs_exclusion_reason ?? '',
    dopDocumentFile: null,
    vocEmissionClass:
      metadata.voc_emission_class === 'A_PLUS' ||
      metadata.voc_emission_class === 'A' ||
      metadata.voc_emission_class === 'B' ||
      metadata.voc_emission_class === 'C' ||
      metadata.voc_emission_class === 'UNTESTED'
        ? metadata.voc_emission_class
        : '',
    eudrDdsReference: metadata.eudr_dds_reference ?? '',
    flameRetardantsPresent: Boolean(metadata.flame_retardants_present),
    flameRetardantChemicalNames: Array.isArray(
      metadata.flame_retardant_chemical_names,
    )
      ? metadata.flame_retardant_chemical_names
      : [],
    ufiCode: metadata.ufi_code ?? '',
    clpHazardPhrases: Array.isArray(metadata.clp_hazard_phrases)
      ? metadata.clp_hazard_phrases
      : [],
    criticalRawMaterials: Boolean(metadata.critical_raw_materials),
    criticalRawMaterialsList: Array.isArray(
      metadata.critical_raw_materials_list,
    )
      ? metadata.critical_raw_materials_list
      : [],
    weeeRegistrationNumber: metadata.weee_registration_number ?? '',
    includeSupplyChainTraceability: Boolean(
      metadata.include_supply_chain_traceability,
    ),
    supplyChainTraceability: Array.isArray(metadata.supply_chain_traceability)
      ? metadata.supply_chain_traceability.map((row) => ({
          tier: row.tier,
          company_name: row.company_name,
          country_code: row.country_code,
          facility_id_or_address: row.facility_id_or_address ?? '',
          process_component: row.process_component,
        }))
      : [],
    includeConsumerPackaging:
      metadata.include_consumer_packaging ?? packaging.length > 0,
    packagingItems: packaging.map((item) => ({
      ...item,
      packaging_category:
        item.packaging_category === 'ecommerce'
          ? 'tertiary'
          : item.packaging_category,
    })),
    packagingEprRegistration,
    documentIds: [
      ...reachDocumentIds,
      ...ceDocumentIds.filter(
        (documentId) =>
          documentId !== notifiedBodyCertificateDocumentId &&
          documentId !== declarationOfConformityDocumentId &&
          documentId !== declarationOfIncorporationDocumentId,
      ),
    ],
    carbonFootprint: toEditableString(circularity?.carbon_footprint_kg_co2e),
    lifespan: toEditableString(circularity?.expected_lifetime_years),
    lifespanUnit:
      metadata.lifespan_unit === 'months' || metadata.lifespan_unit === 'years'
        ? metadata.lifespan_unit
        : 'years',
    repairabilityScore: toEditableString(circularity?.repairability_score),
    demolitionGuideFile: null,
    demolitionGuideUrl: isStorageDocumentPath(metadata.demolition_guide_url)
      ? ''
      : (metadata.demolition_guide_url ?? ''),
    recyclingInstructions: circularity?.recycling_instructions ?? '',
    ratedCapacityAh: toEditableString(circularity?.rated_capacity_ah),
    ratedEnergyKwh: toEditableString(circularity?.rated_energy_kwh),
    nominalVoltageV: toEditableString(circularity?.nominal_voltage_v),
    minimumVoltageV: toEditableString(circularity?.minimum_voltage_v),
    maximumVoltageV: toEditableString(circularity?.maximum_voltage_v),
    powerCapabilityLimits: circularity?.power_capability_limits ?? '',
    roundTripEfficiencyPercent: toEditableString(
      circularity?.round_trip_efficiency_percent,
    ),
    cycleLifetimeDescriptor: circularity?.cycle_lifetime_descriptor ?? '',
    operatingTemperatureWindow: circularity?.operating_temperature_window ?? '',
    extinguishingAgents: circularity?.extinguishing_agents ?? '',
    safetyHandlingMeasures: circularity?.safety_handling_measures ?? '',
    restrictTechnicalSpecs:
      passport?.visibility_settings?.technical_specs_access === 'restricted',
    popsPfasStatus: !popsPfasDeclaration
      ? ''
      : popsPfasDeclaration.status === 'COMPLIANT'
        ? 'verified_compliant'
        : popsPfasDeclaration.svhc_present ||
            popsPfasDeclaration.present_in_component
          ? 'contains_restricted'
          : 'not_verified',
    popsPfasExemptionDetails: popsPfasDeclaration?.present_in_component ?? '',
    waterConsumption: toEditableString(circularity?.water_consumption_liters),
    hasRemovableCovers: Boolean(metadata.has_removable_covers),
    productionRoute:
      metadata.production_route === 'EAF_ELECTRIC_ARC' ||
      metadata.production_route === 'BF_BOF_BLAST_FURNACE' ||
      metadata.production_route === 'RECYCLED_SECONDARY'
        ? metadata.production_route
        : '',
    cbamEmbeddedEmissions: toEditableString(metadata.cbam_embedded_emissions),
    rollingResistanceClass:
      metadata.rolling_resistance_class === 'A' ||
      metadata.rolling_resistance_class === 'B' ||
      metadata.rolling_resistance_class === 'C' ||
      metadata.rolling_resistance_class === 'D' ||
      metadata.rolling_resistance_class === 'E'
        ? metadata.rolling_resistance_class
        : '',
    wetGripClass:
      metadata.wet_grip_class === 'A' ||
      metadata.wet_grip_class === 'B' ||
      metadata.wet_grip_class === 'C' ||
      metadata.wet_grip_class === 'D' ||
      metadata.wet_grip_class === 'E'
        ? metadata.wet_grip_class
        : '',
    abrasionRateMgKm: toEditableString(metadata.abrasion_rate_mg_km),
    isRetreadable: Boolean(metadata.is_retreadable),
    surfactantBiodegradabilityPercentage: toEditableString(
      metadata.surfactant_biodegradability_percentage,
    ),
    colorFastnessRating: toEditableString(circularity?.color_fastness_rating),
    pillingResistanceRating: toEditableString(
      circularity?.pilling_resistance_rating,
    ),
    sparePartsAvailability: circularity?.spare_parts_availability ?? '',
    sparePartsAvailabilityYears: toEditableString(
      circularity?.spare_parts_availability_years,
    ),
    sparePartsCatalogUrl: circularity?.spare_parts_catalog_url ?? '',
    takeBackScheme: circularity?.take_back_scheme ?? '',
    microplasticsStatus:
      circularity?.microplastics_status === 'not_applicable' ||
      circularity?.microplastics_status === 'untested' ||
      circularity?.microplastics_status === 'tested'
        ? circularity.microplastics_status
        : '',
    eprRegistrationNumber: circularity?.epr_registration_number ?? '',
    careSymbols: Array.isArray(circularity?.care_symbols)
      ? circularity.care_symbols
      : [],
    softwareSupportYears: toEditableString(circularity?.software_support_years),
    softwareSupportExpiry: metadata.software_support_expiry ?? '',
    energyEfficiencyRating: metadata.energy_efficiency_rating ?? '',
  };
};

interface AddProductFormProps {
  onCancel: () => void;
  onCreated: (productId: string) => void;
  productId?: string;
}

export default function AddProductForm({
  onCancel,
  onCreated,
  productId: initialProductId,
}: AddProductFormProps) {
  const selectedOrganisation = useAppSelector(
    (state) => state.organisation.selected,
  ) as UserOrganisation | undefined;
  const { user } = useAppSelector((state) => state.auth);
  const { uploadDocument, uploading, error: uploadError } = useUploadDocument();
  const [productId, setProductId] = useState<string | undefined>(
    initialProductId,
  );
  const isEditMode = Boolean(productId);
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<ProductForm>(() =>
    initialForm(selectedOrganisation?.organisation?.name),
  );
  const [echaCandidateSubstances, setEchaCandidateSubstances] = useState<
    EchaCandidateSubstance[]
  >([]);
  const [loadingExistingProduct, setLoadingExistingProduct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<StepValidationErrors>({});
  const [existingProductImageUrl, setExistingProductImageUrl] = useState<
    string | null
  >(null);
  const [existingRepairManualPath, setExistingRepairManualPath] = useState<
    string | null
  >(null);
  const [existingRepairManualSignedUrl, setExistingRepairManualSignedUrl] =
    useState<string | null>(null);
  const [removeExistingRepairManual, setRemoveExistingRepairManual] =
    useState(false);
  const [existingDisassemblyGuidePath, setExistingDisassemblyGuidePath] =
    useState<string | null>(null);
  const [
    existingDisassemblyGuideSignedUrl,
    setExistingDisassemblyGuideSignedUrl,
  ] = useState<string | null>(null);
  const [removeExistingDisassemblyGuide, setRemoveExistingDisassemblyGuide] =
    useState(false);
  const [existingDopDocumentPath, setExistingDopDocumentPath] = useState<
    string | null
  >(null);
  const [existingDopDocumentSignedUrl, setExistingDopDocumentSignedUrl] =
    useState<string | null>(null);
  const [removeExistingDopDocument, setRemoveExistingDopDocument] =
    useState(false);
  const [
    existingRohsSupportingDocumentId,
    setExistingRohsSupportingDocumentId,
  ] = useState<string | null>(null);
  const [
    existingRohsSupportingDocumentName,
    setExistingRohsSupportingDocumentName,
  ] = useState<string | null>(null);
  const [
    existingRohsSupportingDocumentPath,
    setExistingRohsSupportingDocumentPath,
  ] = useState<string | null>(null);
  const [
    existingRohsSupportingDocumentSignedUrl,
    setExistingRohsSupportingDocumentSignedUrl,
  ] = useState<string | null>(null);
  const [
    removeExistingRohsSupportingDocument,
    setRemoveExistingRohsSupportingDocument,
  ] = useState(false);
  const existingRohsSupportingDocumentIdRef = useRef<string | null>(null);
  const removeExistingRohsSupportingDocumentRef = useRef(false);
  const [existingPopsPfasDocumentId, setExistingPopsPfasDocumentId] = useState<
    string | null
  >(null);
  const [existingPopsPfasDocumentName, setExistingPopsPfasDocumentName] =
    useState<string | null>(null);
  const [existingPopsPfasDocumentPath, setExistingPopsPfasDocumentPath] =
    useState<string | null>(null);
  const [
    existingPopsPfasDocumentSignedUrl,
    setExistingPopsPfasDocumentSignedUrl,
  ] = useState<string | null>(null);
  const [removeExistingPopsPfasDocument, setRemoveExistingPopsPfasDocument] =
    useState(false);
  const existingPopsPfasDocumentIdRef = useRef<string | null>(null);
  const removeExistingPopsPfasDocumentRef = useRef(false);
  const [
    existingMachineryDeclarationDocumentId,
    setExistingMachineryDeclarationDocumentId,
  ] = useState<string | null>(null);
  const [
    existingMachineryDeclarationDocumentName,
    setExistingMachineryDeclarationDocumentName,
  ] = useState<string | null>(null);
  const [
    existingMachineryDeclarationDocumentPath,
    setExistingMachineryDeclarationDocumentPath,
  ] = useState<string | null>(null);
  const [
    existingMachineryDeclarationDocumentSignedUrl,
    setExistingMachineryDeclarationDocumentSignedUrl,
  ] = useState<string | null>(null);
  const [
    existingMachineryDeclarationDocumentKind,
    setExistingMachineryDeclarationDocumentKind,
  ] = useState<'doc' | 'doi' | null>(null);
  const [
    removeExistingMachineryDeclarationDocument,
    setRemoveExistingMachineryDeclarationDocument,
  ] = useState(false);
  const existingMachineryDeclarationDocumentIdRef = useRef<string | null>(null);
  const existingMachineryDeclarationDocumentKindRef = useRef<
    'doc' | 'doi' | null
  >(null);
  const removeExistingMachineryDeclarationDocumentRef = useRef(false);
  const [
    existingNotifiedBodyCertificateId,
    setExistingNotifiedBodyCertificateId,
  ] = useState<string | null>(null);
  const [
    existingNotifiedBodyCertificateName,
    setExistingNotifiedBodyCertificateName,
  ] = useState<string | null>(null);
  const [
    existingNotifiedBodyCertificatePath,
    setExistingNotifiedBodyCertificatePath,
  ] = useState<string | null>(null);
  const [
    existingNotifiedBodyCertificateSignedUrl,
    setExistingNotifiedBodyCertificateSignedUrl,
  ] = useState<string | null>(null);
  const [
    removeExistingNotifiedBodyCertificate,
    setRemoveExistingNotifiedBodyCertificate,
  ] = useState(false);
  const existingNotifiedBodyCertificateIdRef = useRef<string | null>(null);
  const removeExistingNotifiedBodyCertificateRef = useRef(false);
  const [
    existingMicroplasticsDocumentName,
    setExistingMicroplasticsDocumentName,
  ] = useState<string | null>(null);
  const [
    existingMicroplasticsDocumentPath,
    setExistingMicroplasticsDocumentPath,
  ] = useState<string | null>(null);
  const [
    existingMicroplasticsDocumentSignedUrl,
    setExistingMicroplasticsDocumentSignedUrl,
  ] = useState<string | null>(null);
  const [
    removeExistingMicroplasticsDocument,
    setRemoveExistingMicroplasticsDocument,
  ] = useState(false);
  const existingMicroplasticsDocumentIdRef = useRef<string | null>(null);
  const removeExistingMicroplasticsDocumentRef = useRef(false);
  const [existingDemolitionGuidePath, setExistingDemolitionGuidePath] =
    useState<string | null>(null);
  const [
    existingDemolitionGuideSignedUrl,
    setExistingDemolitionGuideSignedUrl,
  ] = useState<string | null>(null);
  const [removeExistingDemolitionGuide, setRemoveExistingDemolitionGuide] =
    useState(false);
  const [existingPassport, setExistingPassport] =
    useState<ExistingPassportRecord | null>(null);
  const [skuExistsError, setSkuExistsError] = useState<string | null>(null);
  const [skuCheckPending, setSkuCheckPending] = useState(false);
  const skuCheckRequestIdRef = useRef(0);
  const [gtinExistsError, setGtinExistsError] = useState<string | null>(null);
  const [gtinCheckPending, setGtinCheckPending] = useState(false);
  const gtinCheckRequestIdRef = useRef(0);

  const bomTotal = useMemo(
    () =>
      form.materials.reduce(
        (total, material) => total + (Number(material.weight_percentage) || 0),
        0,
      ),
    [form.materials],
  );
  const categoryName = categoryOptions.find(
    (category) => category.code === form.categoryCode,
  )?.name;
  const isTextile = form.categoryCode === 'TEXTILES_APPAREL';
  const isBattery = form.categoryCode === 'BATTERIES_STORAGE';
  const isElectronics = form.categoryCode === 'ELECTRONICS_ICT';
  const isIndustrial = form.categoryCode === 'INDUSTRIAL_MACHINERY';
  const isConstruction = form.categoryCode === 'CONSTRUCTION_PRODUCTS';
  const isFurniture = form.categoryCode === 'FURNITURE_MATTRESSES';
  const isIronSteel = form.categoryCode === 'IRON_STEEL_METALS';
  const isTiresAutomotive = form.categoryCode === 'TIRES_AUTOMOTIVE';
  const isChemicals = form.categoryCode === 'CHEMICALS';
  const requiresRohs =
    isElectronics || (isIndustrial && form.containsElectricalComponents);
  const isPublishedPassport = existingPassport?.status === 'PUBLISHED';
  const gtinValidation = form.gtinEan ? isValidGtin(form.gtinEan) : null;

  useEffect(() => {
    setProductId(initialProductId);
    setHasUnsavedChanges(false);
  }, [initialProductId]);

  useEffect(() => {
    const normalizedSku = form.skuModel.trim();
    const organisationId = selectedOrganisation?.organisation?.id ?? null;

    if (!organisationId || !normalizedSku) {
      setSkuExistsError(null);
      setSkuCheckPending(false);
      return;
    }

    const requestId = skuCheckRequestIdRef.current + 1;
    skuCheckRequestIdRef.current = requestId;
    setSkuCheckPending(true);

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        let query = supabase
          .from('products')
          .select('id')
          .eq('supplier_id', organisationId)
          .eq('sku_model', normalizedSku)
          .limit(1);

        if (productId) {
          query = query.neq('id', productId);
        }

        const { data, error: skuLookupError } = await query;

        if (skuCheckRequestIdRef.current !== requestId) {
          return;
        }

        if (skuLookupError) {
          setSkuExistsError(
            'Unable to verify whether this SKU / Model Number already exists.',
          );
          setSkuCheckPending(false);
          return;
        }

        setSkuExistsError(
          (data?.length ?? 0) > 0
            ? 'A product with this SKU / Model Number already exists.'
            : null,
        );
        setSkuCheckPending(false);
      })();
    }, SKU_CHECK_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      if (skuCheckRequestIdRef.current === requestId) {
        setSkuCheckPending(false);
      }
    };
  }, [form.skuModel, productId, selectedOrganisation?.organisation?.id]);

  useEffect(() => {
    const normalizedGtin = form.gtinEan.trim();

    if (!normalizedGtin) {
      setGtinExistsError(null);
      setGtinCheckPending(false);
      return;
    }

    if (!isValidGtin(normalizedGtin)) {
      setGtinExistsError(null);
      setGtinCheckPending(false);
      return;
    }

    const requestId = gtinCheckRequestIdRef.current + 1;
    gtinCheckRequestIdRef.current = requestId;
    setGtinCheckPending(true);

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        let query = supabase
          .from('products')
          .select('id')
          .eq('gtin_ean', normalizedGtin)
          .limit(1);

        if (productId) {
          query = query.neq('id', productId);
        }

        const { data, error: gtinLookupError } = await query;

        if (gtinCheckRequestIdRef.current !== requestId) {
          return;
        }

        if (gtinLookupError) {
          setGtinExistsError(
            'Unable to verify whether this GTIN already exists.',
          );
          setGtinCheckPending(false);
          return;
        }

        setGtinExistsError(
          (data?.length ?? 0) > 0
            ? 'A product with this GTIN already exists.'
            : null,
        );
        setGtinCheckPending(false);
      })();
    }, GTIN_CHECK_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      if (gtinCheckRequestIdRef.current === requestId) {
        setGtinCheckPending(false);
      }
    };
  }, [form.gtinEan, productId]);

  const requiredMachineryDeclarationKind = isPartlyCompletedMachinery(
    form.machineClassification,
  )
    ? 'doi'
    : 'doc';
  const hasExistingMatchingMachineryDeclarationDocument = Boolean(
    existingMachineryDeclarationDocumentId &&
    existingMachineryDeclarationDocumentKind ===
      requiredMachineryDeclarationKind &&
    !removeExistingMachineryDeclarationDocument,
  );
  const hasMachineryDeclarationDocument =
    (requiredMachineryDeclarationKind === 'doc' &&
      Boolean(form.declarationOfConformityFile)) ||
    (requiredMachineryDeclarationKind === 'doi' &&
      Boolean(form.declarationOfIncorporationFile)) ||
    hasExistingMatchingMachineryDeclarationDocument;

  const completenessChecks = useMemo(
    () =>
      buildDppCompletenessChecks({
        productName: form.name,
        brandName: form.brandName,
        skuModel: form.skuModel,
        categoryCode: form.categoryCode,
        gtinEan: form.gtinEan,
        imageUrl: existingProductImageUrl,
        materialsCount: form.materials.length,
        materialWeightPercentageTotal: bomTotal,
        includeConsumerPackaging: form.includeConsumerPackaging,
        packagingCount: form.packagingItems.length,
        expectedLifetimeYears: form.lifespan,
        repairabilityScore: form.repairabilityScore,
        recyclingInstructions: form.recyclingInstructions,
        hasDisassemblyGuideDocument:
          Boolean(form.disassemblyGuideFile) ||
          Boolean(
            existingDisassemblyGuidePath && !removeExistingDisassemblyGuide,
          ),
        hasDopDocument:
          Boolean(form.dopDocumentFile) ||
          Boolean(existingDopDocumentPath && !removeExistingDopDocument),
        isTextile,
        isBattery,
        isIndustrial,
        isConstruction,
        isFurniture,
        careSymbolsCount: form.careSymbols.length,
        microplasticsStatus: form.microplasticsStatus,
        materialsContainWoodOrLeather: containsWoodOrLeatherMaterials(
          form.materials,
        ),
        eudrDdsReference: form.eudrDdsReference,
        flameRetardantsPresent: form.flameRetardantsPresent,
        flameRetardantChemicalNamesCount:
          form.flameRetardantChemicalNames.length,
        ratedCapacityAh: form.ratedCapacityAh,
        ratedEnergyKwh: form.ratedEnergyKwh,
        nominalVoltageV: form.nominalVoltageV,
        minimumVoltageV: form.minimumVoltageV,
        maximumVoltageV: form.maximumVoltageV,
        powerCapabilityLimits: form.powerCapabilityLimits,
        roundTripEfficiencyPercent: form.roundTripEfficiencyPercent,
        cycleLifetimeDescriptor: form.cycleLifetimeDescriptor,
        operatingTemperatureWindow: form.operatingTemperatureWindow,
        extinguishingAgents: form.extinguishingAgents,
        safetyHandlingMeasures: form.safetyHandlingMeasures,
        sparePartsAvailabilityYears: form.sparePartsAvailabilityYears,
        softwareSupportYears: form.softwareSupportYears,
      }),
    [
      bomTotal,
      existingDopDocumentPath,
      existingProductImageUrl,
      form.brandName,
      form.careSymbols.length,
      form.categoryCode,
      form.cycleLifetimeDescriptor,
      form.dopDocumentFile,
      form.disassemblyGuideFile,
      form.eudrDdsReference,
      form.extinguishingAgents,
      form.flameRetardantChemicalNames.length,
      form.flameRetardantsPresent,
      form.gtinEan,
      form.includeConsumerPackaging,
      form.lifespan,
      form.materials,
      form.maximumVoltageV,
      form.minimumVoltageV,
      form.microplasticsStatus,
      form.name,
      form.nominalVoltageV,
      form.operatingTemperatureWindow,
      form.packagingItems.length,
      form.powerCapabilityLimits,
      form.ratedCapacityAh,
      form.ratedEnergyKwh,
      form.recyclingInstructions,
      form.repairabilityScore,
      form.sparePartsAvailabilityYears,
      form.roundTripEfficiencyPercent,
      form.safetyHandlingMeasures,
      form.skuModel,
      form.softwareSupportYears,
      existingDisassemblyGuidePath,
      isBattery,
      isConstruction,
      isFurniture,
      isIndustrial,
      isTextile,
      removeExistingDisassemblyGuide,
      removeExistingDopDocument,
    ],
  );
  const { completenessScore: completeness } = useMemo(
    () => calculateDppCompletenessScore(completenessChecks),
    [completenessChecks],
  );

  useEffect(() => {
    const loadSupplierDefaults = async () => {
      setError(null);

      const supplierResult = await supabase
        .from('suppliers')
        .select('id, company_name, street, city, state, postal_code, country')
        .eq('id', selectedOrganisation?.organisation?.id ?? '')
        .maybeSingle();

      if (!supplierResult.error && supplierResult.data?.id) {
        const supplier = supplierResult.data;

        setForm((current) => ({
          ...current,
          manufacturerName:
            current.manufacturerName || supplier.company_name || '',
        }));
      }
    };

    void loadSupplierDefaults();
  }, [selectedOrganisation?.organisation?.id]);

  useEffect(() => {
    const resolveSignedUrl = async (
      path: string | null,
      setUrl: React.Dispatch<React.SetStateAction<string | null>>,
    ) => {
      if (!path) {
        setUrl(null);
        return;
      }

      const { data } = await supabase.storage
        .from('documents')
        .createSignedUrl(path, 60 * 5);

      setUrl(data?.signedUrl ?? null);
    };

    void resolveSignedUrl(
      existingRepairManualPath,
      setExistingRepairManualSignedUrl,
    );
    void resolveSignedUrl(
      existingDisassemblyGuidePath,
      setExistingDisassemblyGuideSignedUrl,
    );
    void resolveSignedUrl(
      existingDopDocumentPath,
      setExistingDopDocumentSignedUrl,
    );
    void resolveSignedUrl(
      existingRohsSupportingDocumentPath,
      setExistingRohsSupportingDocumentSignedUrl,
    );
    void resolveSignedUrl(
      existingPopsPfasDocumentPath,
      setExistingPopsPfasDocumentSignedUrl,
    );
    void resolveSignedUrl(
      existingMachineryDeclarationDocumentPath,
      setExistingMachineryDeclarationDocumentSignedUrl,
    );
    void resolveSignedUrl(
      existingNotifiedBodyCertificatePath,
      setExistingNotifiedBodyCertificateSignedUrl,
    );
    void resolveSignedUrl(
      existingMicroplasticsDocumentPath,
      setExistingMicroplasticsDocumentSignedUrl,
    );
    void resolveSignedUrl(
      existingDemolitionGuidePath,
      setExistingDemolitionGuideSignedUrl,
    );
  }, [
    existingDemolitionGuidePath,
    existingDisassemblyGuidePath,
    existingDopDocumentPath,
    existingMachineryDeclarationDocumentPath,
    existingMicroplasticsDocumentPath,
    existingNotifiedBodyCertificatePath,
    existingPopsPfasDocumentPath,
    existingRepairManualPath,
    existingRohsSupportingDocumentPath,
  ]);

  useEffect(() => {
    if (!initialProductId) {
      return;
    }

    const loadExistingProduct = async () => {
      setLoadingExistingProduct(true);
      setError(null);

      const [
        productResult,
        materialsResult,
        packagingResult,
        declarationsResult,
        circularityResult,
        passportResult,
      ] = await Promise.all([
        supabase
          .from('products')
          .select(
            'id, name, description, sku_model, gtin_ean, brand_name, image_url, metadata, product_categories(code)',
          )
          .eq('id', initialProductId)
          .single(),
        supabase
          .from('product_materials')
          .select(
            'material_name, weight_percentage, recycled_content_percentage, is_recyclable',
          )
          .eq('product_id', initialProductId)
          .order('weight_percentage', { ascending: false }),
        supabase
          .from('product_packaging')
          .select(
            'packaging_category, material_type, weight_grams, pcr_content_percent, recyclability_status, epr_registration_number',
          )
          .eq('product_id', initialProductId)
          .order('created_at', { ascending: true }),
        supabase
          .from('product_declarations')
          .select(
            'declaration_type, status, svhc_present, substance_name, cas_ec_number, present_in_component, scip_number, document_id, documents(title, storage_path)',
          )
          .eq('product_id', initialProductId),
        supabase
          .from('product_circularity')
          .select(
            'carbon_footprint_kg_co2e, expected_lifetime_years, repairability_score, repair_manual_url, disassembly_guide_url, recycling_instructions, rated_capacity_ah, rated_energy_kwh, nominal_voltage_v, minimum_voltage_v, maximum_voltage_v, power_capability_limits, round_trip_efficiency_percent, cycle_lifetime_descriptor, operating_temperature_window, extinguishing_agents, safety_handling_measures, water_consumption_liters, color_fastness_rating, pilling_resistance_rating, spare_parts_availability, spare_parts_availability_years, spare_parts_catalog_url, take_back_scheme, microplastics_status, epr_registration_number, care_symbols, software_support_years',
          )
          .eq('product_id', initialProductId)
          .maybeSingle(),
        supabase
          .from('product_passports')
          .select('status, published_at, visibility_settings')
          .eq('product_id', initialProductId)
          .order('published_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const failingResult = [
        productResult,
        materialsResult,
        packagingResult,
        declarationsResult,
        circularityResult,
        passportResult,
      ].find((result) => result.error);

      if (failingResult?.error) {
        setError(failingResult.error.message);
        setLoadingExistingProduct(false);
        return;
      }

      const existingDeclarations = (declarationsResult.data ??
        []) as ExistingDeclarationRecord[];
      const packagingRows = (packagingResult.data ?? []) as Array<{
        packaging_category: PackagingItem['packaging_category'];
        material_type: PackagingItem['material_type'];
        weight_grams: number;
        pcr_content_percent: number;
        recyclability_status: PackagingItem['recyclability_status'];
        epr_registration_number: string | null;
      }>;

      setExistingProductImageUrl(
        ((productResult.data as ExistingProductRecord | null)?.image_url ??
          null) as string | null,
      );
      const existingPopsPfasDeclaration = existingDeclarations.find(
        (declaration) => declaration.declaration_type === 'POPS_PFAS',
      );
      const existingPopsPfasDocument = getFirstRelation(
        existingPopsPfasDeclaration?.documents,
      );
      setExistingPopsPfasDocumentId(
        existingPopsPfasDeclaration?.document_id ?? null,
      );
      existingPopsPfasDocumentIdRef.current =
        existingPopsPfasDeclaration?.document_id ?? null;
      setExistingPopsPfasDocumentName(existingPopsPfasDocument?.title ?? null);
      setExistingPopsPfasDocumentPath(
        isStorageDocumentPath(existingPopsPfasDocument?.storage_path)
          ? (existingPopsPfasDocument?.storage_path ?? null)
          : null,
      );
      setExistingPopsPfasDocumentSignedUrl(null);
      setRemoveExistingPopsPfasDocument(false);
      removeExistingPopsPfasDocumentRef.current = false;
      const existingProductMetadata =
        (productResult.data as ExistingProductRecord | null)?.metadata ?? {};
      const existingCeDeclarations = existingDeclarations.filter(
        (declaration) => declaration.declaration_type === 'CE_DOC',
      );
      const declarationOfConformityDocumentId =
        existingProductMetadata.declaration_of_conformity_document_id ?? null;
      const declarationOfIncorporationDocumentId =
        existingProductMetadata.declaration_of_incorporation_document_id ??
        null;
      const genericCeDeclarations = existingCeDeclarations.filter(
        (declaration) =>
          declaration.document_id !==
          existingProductMetadata.notified_body_certificate_document_id,
      );
      const fallbackMachineryDeclaration =
        genericCeDeclarations.length === 1
          ? (genericCeDeclarations[0] ?? null)
          : null;
      const existingDeclarationOfConformity = declarationOfConformityDocumentId
        ? (existingCeDeclarations.find(
            (declaration) =>
              declaration.document_id === declarationOfConformityDocumentId,
          ) ?? null)
        : normalizeMachineClassification(
              existingProductMetadata.machine_classification,
            ) !== 'PARTLY_COMPLETED_MACHINERY'
          ? fallbackMachineryDeclaration
          : null;
      const existingDeclarationOfIncorporation =
        declarationOfIncorporationDocumentId
          ? (existingCeDeclarations.find(
              (declaration) =>
                declaration.document_id ===
                declarationOfIncorporationDocumentId,
            ) ?? null)
          : normalizeMachineClassification(
                existingProductMetadata.machine_classification,
              ) === 'PARTLY_COMPLETED_MACHINERY'
            ? fallbackMachineryDeclaration
            : null;
      const existingMachineryDeclaration =
        existingDeclarationOfIncorporation ?? existingDeclarationOfConformity;
      const existingMachineryDeclarationDocument = getFirstRelation(
        existingMachineryDeclaration?.documents,
      );
      const machineryDeclarationKind = existingDeclarationOfIncorporation
        ? 'doi'
        : existingDeclarationOfConformity
          ? 'doc'
          : null;
      setExistingMachineryDeclarationDocumentId(
        existingMachineryDeclaration?.document_id ?? null,
      );
      existingMachineryDeclarationDocumentIdRef.current =
        existingMachineryDeclaration?.document_id ?? null;
      setExistingMachineryDeclarationDocumentName(
        existingMachineryDeclarationDocument?.title ?? null,
      );
      setExistingMachineryDeclarationDocumentPath(
        isStorageDocumentPath(
          existingMachineryDeclarationDocument?.storage_path,
        )
          ? (existingMachineryDeclarationDocument?.storage_path ?? null)
          : null,
      );
      setExistingMachineryDeclarationDocumentSignedUrl(null);
      setExistingMachineryDeclarationDocumentKind(machineryDeclarationKind);
      existingMachineryDeclarationDocumentKindRef.current =
        machineryDeclarationKind;
      setRemoveExistingMachineryDeclarationDocument(false);
      removeExistingMachineryDeclarationDocumentRef.current = false;
      const existingNotifiedBodyDeclaration =
        existingProductMetadata.notified_body_certificate_document_id
          ? (existingCeDeclarations.find(
              (declaration) =>
                declaration.document_id ===
                existingProductMetadata.notified_body_certificate_document_id,
            ) ?? null)
          : existingCeDeclarations.length === 1
            ? (existingCeDeclarations[0] ?? null)
            : null;
      const existingNotifiedBodyDocument = getFirstRelation(
        existingNotifiedBodyDeclaration?.documents,
      );
      setExistingNotifiedBodyCertificateId(
        existingNotifiedBodyDeclaration?.document_id ?? null,
      );
      existingNotifiedBodyCertificateIdRef.current =
        existingNotifiedBodyDeclaration?.document_id ?? null;
      setExistingNotifiedBodyCertificateName(
        existingNotifiedBodyDocument?.title ?? null,
      );
      setExistingNotifiedBodyCertificatePath(
        isStorageDocumentPath(existingNotifiedBodyDocument?.storage_path)
          ? (existingNotifiedBodyDocument?.storage_path ?? null)
          : null,
      );
      setExistingNotifiedBodyCertificateSignedUrl(null);
      setRemoveExistingNotifiedBodyCertificate(false);
      removeExistingNotifiedBodyCertificateRef.current = false;
      const existingRohsDeclaration = existingDeclarations.find(
        (declaration) => declaration.declaration_type === 'ROHS',
      );
      const existingRohsDocument = getFirstRelation(
        existingRohsDeclaration?.documents,
      );
      setExistingRohsSupportingDocumentId(
        existingRohsDeclaration?.document_id ?? null,
      );
      existingRohsSupportingDocumentIdRef.current =
        existingRohsDeclaration?.document_id ?? null;
      setExistingRohsSupportingDocumentName(
        existingRohsDocument?.title ?? null,
      );
      setExistingRohsSupportingDocumentPath(
        isStorageDocumentPath(existingRohsDocument?.storage_path)
          ? (existingRohsDocument?.storage_path ?? null)
          : null,
      );
      setExistingRohsSupportingDocumentSignedUrl(null);
      setRemoveExistingRohsSupportingDocument(false);
      removeExistingRohsSupportingDocumentRef.current = false;
      const existingMicroplasticsDeclaration = existingDeclarations.find(
        (declaration) => declaration.declaration_type === 'MICROPLASTICS',
      );
      const existingMicroplasticsDocument = getFirstRelation(
        existingMicroplasticsDeclaration?.documents,
      );
      existingMicroplasticsDocumentIdRef.current =
        existingMicroplasticsDeclaration?.document_id ?? null;
      setExistingMicroplasticsDocumentName(
        existingMicroplasticsDocument?.title ?? null,
      );
      setExistingMicroplasticsDocumentPath(
        isStorageDocumentPath(existingMicroplasticsDocument?.storage_path)
          ? (existingMicroplasticsDocument?.storage_path ?? null)
          : null,
      );
      setExistingMicroplasticsDocumentSignedUrl(null);
      setRemoveExistingMicroplasticsDocument(false);
      removeExistingMicroplasticsDocumentRef.current = false;
      const existingCircularity =
        (circularityResult.data as ExistingCircularityRecord | null) ?? null;
      setExistingRepairManualPath(
        isStorageDocumentPath(existingCircularity?.repair_manual_url)
          ? (existingCircularity?.repair_manual_url ?? null)
          : null,
      );
      setExistingDisassemblyGuidePath(
        isStorageDocumentPath(existingCircularity?.disassembly_guide_url)
          ? (existingCircularity?.disassembly_guide_url ?? null)
          : null,
      );
      setExistingDopDocumentPath(
        isStorageDocumentPath(existingProductMetadata.dop_document_path)
          ? (existingProductMetadata.dop_document_path ?? null)
          : null,
      );
      setExistingDopDocumentSignedUrl(null);
      setRemoveExistingDopDocument(false);
      setExistingDemolitionGuidePath(
        isStorageDocumentPath(existingProductMetadata.demolition_guide_url)
          ? (existingProductMetadata.demolition_guide_url ?? null)
          : null,
      );
      setExistingDemolitionGuideSignedUrl(null);
      setRemoveExistingDemolitionGuide(false);
      setRemoveExistingRepairManual(false);
      setRemoveExistingDisassemblyGuide(false);
      setForm(
        buildFormFromExistingData({
          product: productResult.data as ExistingProductRecord,
          materials: (
            (materialsResult.data ?? []) as Array<{
              material_name: string;
              weight_percentage: number;
              recycled_content_percentage: number | null;
              is_recyclable: boolean;
            }>
          ).map((material) => ({
            material_name: material.material_name,
            weight_percentage: toEditableString(material.weight_percentage),
            recycled_content_percentage: toEditableString(
              material.recycled_content_percentage,
            ),
            is_recyclable: material.is_recyclable,
          })),
          packaging: packagingRows.map((item) => ({
            packaging_category: item.packaging_category,
            material_type: item.material_type,
            weight_grams: toEditableString(item.weight_grams),
            pcr_content_percent: toEditableString(item.pcr_content_percent),
            recyclability_status: item.recyclability_status,
          })),
          packagingEprRegistration:
            packagingRows.find((item) => item.epr_registration_number)
              ?.epr_registration_number ?? '',
          declarations: existingDeclarations,
          circularity: existingCircularity,
          passport:
            (passportResult.data as ExistingPassportRecord | null) ?? null,
          organisationName: selectedOrganisation?.organisation?.name,
        }),
      );
      setExistingPassport(
        (passportResult.data as ExistingPassportRecord | null) ?? null,
      );
      setHasUnsavedChanges(false);
      setLoadingExistingProduct(false);
    };

    void loadExistingProduct();
  }, [initialProductId, selectedOrganisation?.organisation?.name]);

  const updateForm = <Key extends keyof ProductForm>(
    key: Key,
    value: ProductForm[Key],
    options?: { markDirty?: boolean },
  ) => {
    setValidationErrors({});
    setError(null);
    if (options?.markDirty ?? true) {
      setHasUnsavedChanges(true);
    }
    setForm((current) => ({ ...current, [key]: value }));
  };

  const uploadGuideForExistingProduct = async (
    file: File,
    field: 'repair_manual_url' | 'disassembly_guide_url',
  ) => {
    if (!productId || !isEditMode) return null;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setError('Your organisation session is not available.');
      return null;
    }

    const result = await uploadDocument(file, organisationId, user.id);
    if (!result) return null;

    const { error: updateError } = await supabase
      .from('product_circularity')
      .upsert(
        {
          product_id: productId,
          [field]: result.storagePath,
        },
        { onConflict: 'product_id' },
      );

    if (updateError) {
      setError(updateError.message);
      return null;
    }

    return result.storagePath;
  };

  const uploadRohsDocumentForExistingProduct = async (file: File) => {
    if (!productId || !isEditMode) return null;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setError('Your organisation session is not available.');
      return null;
    }

    const result = await uploadDocument(file, organisationId, user.id);
    if (!result) return null;

    return result;
  };

  const uploadPopsPfasDocumentForExistingProduct = async (file: File) => {
    if (!productId || !isEditMode) return null;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setError('Your organisation session is not available.');
      return null;
    }

    const result = await uploadDocument(file, organisationId, user.id);
    if (!result) return null;

    return result;
  };

  const uploadMachineryDeclarationForExistingProduct = async (file: File) => {
    if (!productId || !isEditMode) return null;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setError('Your organisation session is not available.');
      return null;
    }

    const result = await uploadDocument(file, organisationId, user.id);
    if (!result) return null;

    return result;
  };

  const uploadNotifiedBodyCertificateForExistingProduct = async (
    file: File,
  ) => {
    if (!productId || !isEditMode) return null;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setError('Your organisation session is not available.');
      return null;
    }

    const result = await uploadDocument(file, organisationId, user.id);
    if (!result) return null;

    return result;
  };

  const uploadMicroplasticsDocumentForExistingProduct = async (file: File) => {
    if (!productId || !isEditMode) return null;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setError('Your organisation session is not available.');
      return null;
    }

    const result = await uploadDocument(file, organisationId, user.id);
    if (!result) return null;

    return result;
  };

  const removeGuideFromExistingProduct = async (
    field: 'repair_manual_url' | 'disassembly_guide_url',
  ) => {
    if (!productId || !isEditMode) return true;

    const { error: updateError } = await supabase
      .from('product_circularity')
      .upsert(
        {
          product_id: productId,
          [field]: null,
        },
        { onConflict: 'product_id' },
      );

    if (updateError) {
      setError(updateError.message);
      return false;
    }

    return true;
  };

  const changeCategory = (categoryCode: CategoryCode | '') => {
    setValidationErrors({});
    setError(null);
    setHasUnsavedChanges(true);
    setForm((current) => ({
      ...current,
      categoryCode,
      seasonCollection: '',
      targetAudience: [],
      hardwareRevision: '',
      machineClassification: '',
      ceResponsiblePerson: '',
      declarationOfConformityFile: null,
      declarationOfIncorporationFile: null,
      notifiedBodyNumber: '',
      notifiedBodyCertNumber: '',
      notifiedBodyCertificateFile: null,
      ceResponsibleEntityType: '',
      ceResponsibleCompanyName: '',
      ceResponsibleEuAddress: '',
      ceResponsibleContactEmail: '',
      containsElectricalComponents: false,
      rohsStatus: '',
      rohsExemptionCode: '',
      customExemptionDetails: '',
      rohsExclusionReason: '',
      rohsSupportingDocumentFile: null,
      dopDocumentFile: null,
      vocEmissionClass: '',
      eudrDdsReference: '',
      flameRetardantsPresent: false,
      flameRetardantChemicalNames: [],
      ufiCode: '',
      clpHazardPhrases: [],
      criticalRawMaterials: false,
      criticalRawMaterialsList: [],
      weeeRegistrationNumber: '',
      popsPfasStatus: '',
      popsPfasTestReportFile: null,
      popsPfasExemptionDetails: '',
      includeConsumerPackaging: true,
      packagingItems: [],
      packagingEprRegistration: '',
      demolitionGuideFile: null,
      demolitionGuideUrl: '',
      careSymbols: [],
      colorFastnessRating: '',
      pillingResistanceRating: '',
      waterConsumption: '',
      hasRemovableCovers: false,
      productionRoute: '',
      cbamEmbeddedEmissions: '',
      rollingResistanceClass: '',
      wetGripClass: '',
      abrasionRateMgKm: '',
      isRetreadable: false,
      surfactantBiodegradabilityPercentage: '',
      sparePartsAvailability: '',
      sparePartsAvailabilityYears: '',
      sparePartsCatalogUrl: '',
      takeBackScheme: '',
      microplasticsStatus: '',
      microplasticsReportFile: null,
      eprRegistrationNumber: '',
      softwareSupportYears: '',
      softwareSupportExpiry: '',
      energyEfficiencyRating: '',
    }));
  };

  const updateMaterial = <Key extends keyof Material>(
    index: number,
    key: Key,
    value: Material[Key],
  ) => {
    setValidationErrors({});
    setError(null);
    setHasUnsavedChanges(true);
    setForm((current) => ({
      ...current,
      materials: current.materials.map((material, materialIndex) =>
        materialIndex === index ? { ...material, [key]: value } : material,
      ),
    }));
  };

  const addSupplyChainSupplier = () => {
    updateForm('supplyChainTraceability', [
      ...form.supplyChainTraceability,
      {
        tier: 'tier_1',
        company_name: '',
        country_code: '',
        facility_id_or_address: '',
        process_component: '',
      },
    ]);
  };

  const updateSupplyChainSupplier = <
    Key extends keyof SupplyChainTraceabilityRow,
  >(
    index: number,
    key: Key,
    value: SupplyChainTraceabilityRow[Key],
  ) => {
    updateForm(
      'supplyChainTraceability',
      form.supplyChainTraceability.map((supplier, supplierIndex) =>
        supplierIndex === index ? { ...supplier, [key]: value } : supplier,
      ),
    );
  };

  const addSvhcDeclaration = () => {
    setEchaCandidateSubstances([]);
    updateForm('svhcDeclarations', [
      ...form.svhcDeclarations,
      {
        substance_name: '',
        cas_ec_number: '',
        present_in_component: '',
        scip_dossier_number: '',
      },
    ]);
  };

  const updateSvhcDeclaration = <Key extends keyof SvhcDeclarationRow>(
    index: number,
    key: Key,
    value: SvhcDeclarationRow[Key],
  ) => {
    updateForm(
      'svhcDeclarations',
      form.svhcDeclarations.map((declaration, declarationIndex) =>
        declarationIndex === index
          ? { ...declaration, [key]: value }
          : declaration,
      ),
    );
  };

  const addPackagingItem = () => {
    updateForm('packagingItems', [
      ...form.packagingItems,
      {
        packaging_category: '',
        material_type: '',
        weight_grams: '',
        pcr_content_percent: '0',
        recyclability_status: '',
      },
    ]);
  };

  const updatePackagingItem = <Key extends keyof PackagingItem>(
    index: number,
    key: Key,
    value: PackagingItem[Key],
  ) => {
    updateForm(
      'packagingItems',
      form.packagingItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    );
  };

  const handleSvhcSubstanceChange = (index: number, substanceName: string) => {
    const match = echaCandidateSubstances.find(
      (substance) => substance.substance_name === substanceName,
    );
    const identifier = [
      match?.ec_number ? `EC ${match.ec_number}` : null,
      match?.cas_number ? `CAS ${match.cas_number}` : null,
    ]
      .filter(Boolean)
      .join(' / ');

    updateForm(
      'svhcDeclarations',
      form.svhcDeclarations.map((declaration, declarationIndex) =>
        declarationIndex === index
          ? {
              ...declaration,
              substance_name: substanceName,
              cas_ec_number: identifier || declaration.cas_ec_number,
            }
          : declaration,
      ),
    );

    // Exact catalog match selected — clear suggestions so the next row starts fresh
    if (match) {
      setEchaCandidateSubstances([]);
      return;
    }

    if (substanceName.trim().length < 2) {
      setEchaCandidateSubstances([]);
      return;
    }

    void (async () => {
      const { data, error: searchError } = await supabase
        .from('echa_candidate_list')
        .select('substance_name, ec_number, cas_number')
        .ilike('substance_name', `%${substanceName.trim()}%`)
        .order('substance_name')
        .limit(20);

      if (searchError) {
        console.error('Error searching ECHA Candidate List:', searchError);
        return;
      }

      setEchaCandidateSubstances((data ?? []) as EchaCandidateSubstance[]);
    })();
  };

  const hasPopsPfasSupportingDocument =
    Boolean(form.popsPfasTestReportFile) ||
    Boolean(existingPopsPfasDocumentId && !removeExistingPopsPfasDocument);
  const hasNotifiedBodyCertificateDocument =
    Boolean(form.notifiedBodyCertificateFile) ||
    Boolean(
      existingNotifiedBodyCertificateId &&
      !removeExistingNotifiedBodyCertificate,
    );
  const hasRohsSupportingDocument =
    Boolean(form.rohsSupportingDocumentFile) ||
    Boolean(
      existingRohsSupportingDocumentId && !removeExistingRohsSupportingDocument,
    );
  const hasDisassemblyGuideDocument =
    Boolean(form.disassemblyGuideFile) ||
    Boolean(existingDisassemblyGuidePath && !removeExistingDisassemblyGuide);
  const footerValidationMessage =
    Object.keys(validationErrors).length > 0
      ? 'Fix errors on this page before continuing'
      : null;

  const validateStep = (stepToValidate: Step) => {
    const fieldErrors: StepValidationErrors = {};

    if (stepToValidate === 1) {
      if (!form.name.trim()) {
        fieldErrors.name = 'Product name is required.';
      }
      if (!form.skuModel.trim()) {
        fieldErrors.skuModel = 'SKU / Model Number is required.';
      } else if (skuCheckPending) {
        fieldErrors.skuModel =
          'Wait for SKU / Model Number validation to finish.';
      } else if (skuExistsError) {
        fieldErrors.skuModel = skuExistsError;
      }
      if (!form.brandName.trim()) {
        fieldErrors.brandName = 'Brand name is required.';
      }
      if (!form.categoryCode) {
        fieldErrors.categoryCode = 'Select an EU product category.';
      }
      if (!form.gtinEan.trim()) {
        fieldErrors.gtinEan = 'GTIN / EAN is required for publication.';
      } else if (!isValidGtin(form.gtinEan)) {
        fieldErrors.gtinEan =
          'GTIN / EAN must be a valid 13 or 14-digit GS1 code.';
      } else if (gtinCheckPending) {
        fieldErrors.gtinEan = 'Wait for GTIN validation to finish.';
      } else if (gtinExistsError) {
        fieldErrors.gtinEan = gtinExistsError;
      }
      if (!form.manufacturerName.trim()) {
        fieldErrors.manufacturerName = 'Manufacturer legal name is required.';
      }
      if (!form.manufacturerAddress.trim()) {
        fieldErrors.manufacturerAddress = 'Manufacturer address is required.';
      }
      if (form.importedOutsideEu && !form.importerName.trim()) {
        fieldErrors.importerName = 'EU importer name is required.';
      }
      if (form.importedOutsideEu && !form.importerEmail.trim()) {
        fieldErrors.importerEmail = 'EU importer email is required.';
      } else if (
        form.importedOutsideEu &&
        form.importerEmail.trim() &&
        !isValidEmail(form.importerEmail.trim())
      ) {
        fieldErrors.importerEmail = 'Enter a valid EU importer email.';
      }
      if (form.importedOutsideEu && !form.importerAddress.trim()) {
        fieldErrors.importerAddress = 'EU importer address is required.';
      }
      if (form.importedOutsideEu && !form.importerEori.trim()) {
        fieldErrors.importerEori = 'EORI Number is required.';
      } else if (
        form.importedOutsideEu &&
        form.importerEori.trim() &&
        !isValidEori(form.importerEori.trim())
      ) {
        fieldErrors.importerEori =
          'EORI Number must start with a 2-letter country code followed by an identifier.';
      }
      if (isIndustrial) {
        const usesDeclarationOfIncorporation = isPartlyCompletedMachinery(
          form.machineClassification,
        );
        const declarationField = getMachineryDeclarationField(
          form.machineClassification,
        );
        const selectedMachineryDeclarationFile =
          declarationField === 'declarationOfConformityFile'
            ? form.declarationOfConformityFile
            : form.declarationOfIncorporationFile;
        const usesAuthorizedRepresentative =
          form.ceResponsibleEntityType === 'AUTHORIZED_REPRESENTATIVE';
        const requiresResponsiblePersonCard = usesAuthorizedRepresentative;
        const hasInvalidResponsiblePersonEmail =
          Boolean(form.ceResponsibleContactEmail.trim()) &&
          !isValidEmail(form.ceResponsibleContactEmail.trim());

        if (!form.machineClassification) {
          fieldErrors.machineClassification =
            'Select a CE machine classification.';
        }
        if (
          requiresResponsiblePersonCard &&
          !form.ceResponsibleCompanyName.trim()
        ) {
          fieldErrors.ceResponsibleCompanyName = form.importedOutsideEu
            ? 'EU responsible person company name is required.'
            : 'Authorized representative company name is required.';
        }
        if (
          requiresResponsiblePersonCard &&
          !form.ceResponsibleEuAddress.trim()
        ) {
          fieldErrors.ceResponsibleEuAddress = form.importedOutsideEu
            ? 'EU responsible person address is required.'
            : 'Authorized representative address is required.';
        }
        if (
          requiresResponsiblePersonCard &&
          !form.ceResponsibleContactEmail.trim()
        ) {
          fieldErrors.ceResponsibleContactEmail = form.importedOutsideEu
            ? 'EU responsible person contact email is required.'
            : 'Authorized representative contact email is required.';
        } else if (
          requiresResponsiblePersonCard &&
          hasInvalidResponsiblePersonEmail
        ) {
          fieldErrors.ceResponsibleContactEmail = form.importedOutsideEu
            ? 'Enter a valid EU responsible person contact email.'
            : 'Enter a valid authorized representative contact email.';
        }

        if (
          !form.machineClassification ||
          (requiresResponsiblePersonCard &&
            (!form.ceResponsibleCompanyName.trim() ||
              !form.ceResponsibleEuAddress.trim() ||
              !form.ceResponsibleContactEmail.trim() ||
              hasInvalidResponsiblePersonEmail))
        ) {
          fieldErrors.industrialMachineryRequirements =
            'Industrial machinery requires a CE Machine Classification and an established EU Responsible Person.';
        }

        if (!hasMachineryDeclarationDocument) {
          fieldErrors[declarationField] = usesDeclarationOfIncorporation
            ? 'EU Declaration of Incorporation PDF is required.'
            : 'EU Declaration of Conformity PDF is required.';
        } else if (
          selectedMachineryDeclarationFile &&
          !isPdfFile(selectedMachineryDeclarationFile)
        ) {
          fieldErrors[declarationField] = usesDeclarationOfIncorporation
            ? 'Upload the EU Declaration of Incorporation as a PDF.'
            : 'Upload the EU Declaration of Conformity as a PDF.';
        } else if (
          selectedMachineryDeclarationFile &&
          selectedMachineryDeclarationFile.size >
            MACHINERY_DECLARATION_MAX_BYTES
        ) {
          fieldErrors[declarationField] = usesDeclarationOfIncorporation
            ? 'EU Declaration of Incorporation PDF must be 10MB or less.'
            : 'EU Declaration of Conformity PDF must be 10MB or less.';
        }

        if (form.machineClassification === 'HIGH_RISK_MACHINERY') {
          const hasInvalidNotifiedBodyNumber = !/^\d{4}$/.test(
            form.notifiedBodyNumber.trim(),
          );
          const selectedNotifiedBodyCertificate =
            form.notifiedBodyCertificateFile;
          const hasInvalidCertificateFile =
            Boolean(selectedNotifiedBodyCertificate) &&
            !isPdfFile(selectedNotifiedBodyCertificate as File);

          if (hasInvalidNotifiedBodyNumber) {
            fieldErrors.notifiedBodyNumber =
              'Notified Body Number must be exactly 4 digits.';
          }
          if (!form.notifiedBodyCertNumber.trim()) {
            fieldErrors.notifiedBodyCertNumber =
              'Notified Body Certificate Number is required.';
          }
          if (!hasNotifiedBodyCertificateDocument) {
            fieldErrors.notifiedBodyCertificateFile =
              'EU Type-Examination Certificate PDF is required.';
          } else if (hasInvalidCertificateFile) {
            fieldErrors.notifiedBodyCertificateFile =
              'Upload the EU Type-Examination Certificate as a PDF.';
          }

          if (
            hasInvalidNotifiedBodyNumber ||
            !form.notifiedBodyCertNumber.trim() ||
            !hasNotifiedBodyCertificateDocument ||
            hasInvalidCertificateFile
          ) {
            fieldErrors.highRiskMachineryRequirements =
              'High-risk machinery requires a 4-digit Notified Body ID, Certificate Number, and an attached EU Type-Examination Certificate PDF.';
          }
        }
      }
    }

    if (stepToValidate === 2) {
      if (!form.svhcStatus) {
        fieldErrors.svhcStatus = 'Select a REACH SVHC status.';
      }
      if (form.materials.length === 0) {
        fieldErrors.materials =
          'Add bill-of-materials entries totaling 100% by weight.';
      } else if (bomTotal > 100) {
        fieldErrors.materials = 'Total material breakdown cannot exceed 100%.';
      } else if (Math.abs(bomTotal - 100) >= 0.01) {
        fieldErrors.materials =
          'Total material breakdown must equal 100% for publication.';
      }
      if (form.includeConsumerPackaging && form.packagingItems.length === 0) {
        fieldErrors.packagingItems =
          'Add at least one packaging entry for publication.';
      }
      if (requiresRohs && !form.rohsStatus) {
        fieldErrors.rohsStatus = isIndustrial
          ? 'RoHS compliance status is required when electrical or electronic components are present.'
          : 'RoHS compliance status is required for electronics.';
      }
      if (requiresRohs && form.rohsStatus === 'with_exemption') {
        if (!form.rohsExemptionCode) {
          fieldErrors.rohsExemptionCode = 'Select a RoHS exemption code.';
        }

        if (
          form.rohsExemptionCode === 'OTHER' &&
          !form.customExemptionDetails.trim()
        ) {
          fieldErrors.customExemptionDetails =
            'Custom exemption details are required when OTHER is selected.';
        }
      }
      if (
        requiresRohs &&
        form.rohsStatus === 'not_applicable' &&
        !form.rohsExclusionReason.trim()
      ) {
        fieldErrors.rohsExclusionReason = isIndustrial
          ? 'Select a valid RoHS exclusion reason when RoHS applies to this machinery.'
          : 'Select a valid RoHS exclusion reason for electronics.';
      }
      if (
        requiresRohs &&
        (form.rohsStatus === 'without_exemption' ||
          form.rohsStatus === 'with_exemption') &&
        !hasRohsSupportingDocument
      ) {
        fieldErrors.rohsSupportingDocumentFile =
          'RoHS supporting document is required.';
      }
      if (
        (isElectronics || isBattery || isIndustrial) &&
        form.criticalRawMaterials &&
        form.criticalRawMaterialsList.length === 0
      ) {
        fieldErrors.criticalRawMaterialsList =
          'Select at least one critical raw material when CRM is present.';
      }
      if (
        (isTextile || isBattery) &&
        form.popsPfasStatus === 'verified_compliant' &&
        !hasPopsPfasSupportingDocument
      ) {
        fieldErrors.popsPfasTestReportFile =
          'PFAS/POPs Test Report or Supplier Self-Declaration is required when marked as Verified Compliant.';
      }
      if (
        (isTextile || isBattery) &&
        form.popsPfasStatus === 'contains_restricted' &&
        !form.popsPfasExemptionDetails.trim()
      ) {
        fieldErrors.popsPfasExemptionDetails =
          'Substance and exemption clause details are required when Contains Restricted PFAS is selected.';
      }
      if (
        form.svhcStatus === 'contains' &&
        !form.svhcDeclarations.some((declaration) =>
          declaration.substance_name.trim(),
        )
      ) {
        fieldErrors.svhcDeclarations =
          'Add at least one SVHC declaration with a substance name.';
      }

      if (isConstruction) {
        if (
          !form.dopDocumentFile &&
          !(existingDopDocumentPath && !removeExistingDopDocument)
        ) {
          fieldErrors.dopDocumentFile =
            'Declaration of Performance (DoP) PDF is required.';
        } else if (form.dopDocumentFile && !isPdfFile(form.dopDocumentFile)) {
          fieldErrors.dopDocumentFile =
            'Upload the Declaration of Performance as a PDF.';
        }
      }

      if (
        isFurniture &&
        containsWoodOrLeatherMaterials(form.materials) &&
        !form.eudrDdsReference.trim()
      ) {
        fieldErrors.eudrDdsReference =
          'EUDR DDS reference is required when wood or leather materials are present.';
      }

      if (
        isFurniture &&
        form.flameRetardantsPresent &&
        form.flameRetardantChemicalNames.length === 0
      ) {
        fieldErrors.flameRetardantChemicalNames =
          'Select at least one flame retardant chemical when flame retardants are present.';
      }

      if (
        isChemicals &&
        form.ufiCode.trim() &&
        !/^[A-Z0-9]{4}(?:-[A-Z0-9]{4}){3}$/.test(form.ufiCode.trim())
      ) {
        fieldErrors.ufiCode =
          'UFI code must use the format XXXX-XXXX-XXXX-XXXX.';
      }
    }

    if (stepToValidate === 3) {
      if (!form.lifespan.trim()) {
        fieldErrors.lifespan =
          'Expected product lifespan is required for publication.';
      } else if ((asOptionalNumber(form.lifespan) ?? 0) <= 0) {
        fieldErrors.lifespan =
          'Expected product lifespan must be greater than 0.';
      }

      if (!form.repairabilityScore.trim()) {
        fieldErrors.repairabilityScore =
          'Repairability score is required for publication.';
      } else if (asOptionalNumber(form.repairabilityScore) === undefined) {
        fieldErrors.repairabilityScore =
          'Repairability score must be a valid number.';
      }

      if (!form.recyclingInstructions.trim()) {
        fieldErrors.recyclingInstructions =
          'Recycling & end-of-life instructions are required.';
      } else if (isElectronics) {
        const recyclingInstructionsError =
          getElectronicsRecyclingInstructionsError(form.recyclingInstructions);

        if (recyclingInstructionsError) {
          fieldErrors.recyclingInstructions = recyclingInstructionsError;
        }
      }

      if (isConstruction) {
        if (form.demolitionGuideFile && !isPdfFile(form.demolitionGuideFile)) {
          fieldErrors.demolitionGuideFile =
            'Upload the demolition guide as a PDF.';
        }

        if (
          form.demolitionGuideUrl.trim() &&
          !isValidUrl(form.demolitionGuideUrl.trim())
        ) {
          fieldErrors.demolitionGuideUrl =
            'Demolition guide URL must be a valid http or https URL.';
        }
      }

      if (isTextile && form.careSymbols.length === 0) {
        fieldErrors.careSymbols =
          'Select at least one ISO 3758 care symbol for publication.';
      }

      if (isTextile && !form.microplasticsStatus) {
        fieldErrors.microplasticsStatus =
          'Select a microplastics release declaration.';
      }

      if (
        isIronSteel &&
        form.cbamEmbeddedEmissions.trim() &&
        (asOptionalNumber(form.cbamEmbeddedEmissions) ?? 0) < 0
      ) {
        fieldErrors.cbamEmbeddedEmissions =
          'CBAM embedded emissions cannot be negative.';
      }

      if (
        isTiresAutomotive &&
        form.abrasionRateMgKm.trim() &&
        (asOptionalNumber(form.abrasionRateMgKm) ?? 0) < 0
      ) {
        fieldErrors.abrasionRateMgKm = 'Abrasion rate cannot be negative.';
      }

      if (
        isChemicals &&
        form.surfactantBiodegradabilityPercentage.trim() &&
        ((asOptionalNumber(form.surfactantBiodegradabilityPercentage) ?? 0) <
          0 ||
          (asOptionalNumber(form.surfactantBiodegradabilityPercentage) ?? 0) >
            100)
      ) {
        fieldErrors.surfactantBiodegradabilityPercentage =
          'Surfactant biodegradability percentage must be between 0 and 100.';
      }
    }

    if (stepToValidate === 3 && isElectronics) {
      if (!hasDisassemblyGuideDocument) {
        fieldErrors.disassemblyGuideFile =
          'Disassembly Instructions PDF is required for electronics.';
      }
    }

    if (stepToValidate === 3 && isIndustrial) {
      if (!form.sparePartsAvailabilityYears.trim()) {
        fieldErrors.sparePartsAvailabilityYears =
          'Guaranteed spare parts availability years are required for industrial machinery.';
      } else if (
        (asOptionalNumber(form.sparePartsAvailabilityYears) ?? 0) <= 0
      ) {
        fieldErrors.sparePartsAvailabilityYears =
          'Guaranteed spare parts availability years must be greater than 0.';
      }

      if (
        form.sparePartsCatalogUrl.trim() &&
        !isValidUrl(form.sparePartsCatalogUrl.trim())
      ) {
        fieldErrors.sparePartsCatalogUrl =
          'Spare parts catalog URL must be a valid http or https URL.';
      }

      if (!form.softwareSupportYears.trim()) {
        fieldErrors.softwareSupportYears =
          'Guaranteed software support years are required for industrial machinery.';
      } else if ((asOptionalNumber(form.softwareSupportYears) ?? 0) <= 0) {
        fieldErrors.softwareSupportYears =
          'Guaranteed software support years must be greater than 0.';
      }
    }

    if (stepToValidate === 3 && isBattery) {
      Object.assign(fieldErrors, getBatteryTechnicalDataErrors(form));
    }

    return fieldErrors;
  };

  const resolveCategoryId = async () => {
    if (!form.categoryCode) throw new Error('Select a product category.');

    const { data, error: categoryLookupError } = await supabase
      .from('product_categories')
      .select('id')
      .eq('code', form.categoryCode)
      .maybeSingle();

    if (categoryLookupError) throw categoryLookupError;
    if (!data?.id) {
      throw new Error(
        `Product category ${form.categoryCode} is not configured in the database. Run the latest product categories migration in Supabase admin.`,
      );
    }
    return data.id as string;
  };

  const persistDraft = async ({
    exitAfterSave,
    nextStepAfterSave,
  }: {
    exitAfterSave: boolean;
    nextStepAfterSave?: Step;
  }) => {
    setSaving(true);
    setValidationErrors({});
    setError(null);

    try {
      const organisationId = selectedOrganisation?.organisation?.id;
      if (!organisationId || !user?.id) {
        throw new Error('Your organisation session is not available.');
      }

      const wasEditingExistingProduct = Boolean(productId);
      const categoryId = await resolveCategoryId();
      const productMetadata = buildProductMetadata(form, {
        declarationOfConformityDocumentId:
          existingMachineryDeclarationDocumentKindRef.current === 'doc' &&
          !removeExistingMachineryDeclarationDocumentRef.current
            ? existingMachineryDeclarationDocumentIdRef.current
            : null,
        declarationOfIncorporationDocumentId:
          existingMachineryDeclarationDocumentKindRef.current === 'doi' &&
          !removeExistingMachineryDeclarationDocumentRef.current
            ? existingMachineryDeclarationDocumentIdRef.current
            : null,
        dopDocumentPath:
          existingDopDocumentPath && !removeExistingDopDocument
            ? existingDopDocumentPath
            : null,
        demolitionGuideUrl:
          form.demolitionGuideUrl.trim() ||
          (existingDemolitionGuidePath && !removeExistingDemolitionGuide
            ? existingDemolitionGuidePath
            : null),
        notifiedBodyCertificateDocumentId:
          existingNotifiedBodyCertificateIdRef.current,
      });
      let savedProductId = productId;

      if (wasEditingExistingProduct) {
        const { error: updateProductError } = await supabase
          .from('products')
          .update({
            supplier_id: organisationId,
            name: form.name.trim(),
            description: form.productSummary.trim() || null,
            sku_model: form.skuModel.trim(),
            gtin_ean: form.gtinEan.trim() || null,
            brand_name: form.brandName.trim(),
            category_id: categoryId,
            metadata: productMetadata,
          })
          .eq('id', productId);
        if (updateProductError) throw updateProductError;
      } else {
        const { data: createdProduct, error: productError } = await supabase
          .from('products')
          .insert({
            supplier_id: organisationId,
            name: form.name.trim(),
            description: form.productSummary.trim() || null,
            sku_model: form.skuModel.trim(),
            gtin_ean: form.gtinEan.trim() || null,
            brand_name: form.brandName.trim(),
            category_id: categoryId,
            metadata: productMetadata,
          })
          .select('id')
          .single();
        if (productError) throw productError;
        savedProductId = createdProduct.id;
      }

      if (!savedProductId) {
        throw new Error('Product ID is missing.');
      }

      let repairManualUrl =
        existingRepairManualPath && !removeExistingRepairManual
          ? existingRepairManualPath
          : null;
      if (form.repairManualFile) {
        const result = await uploadDocument(
          form.repairManualFile,
          organisationId,
          user.id,
        );
        if (!result) throw new Error('Repair manual upload failed.');
        repairManualUrl = result.storagePath;
      }

      let disassemblyGuideUrl =
        existingDisassemblyGuidePath && !removeExistingDisassemblyGuide
          ? existingDisassemblyGuidePath
          : null;
      if (form.disassemblyGuideFile) {
        const result = await uploadDocument(
          form.disassemblyGuideFile,
          organisationId,
          user.id,
        );
        if (!result) throw new Error('Disassembly guide upload failed.');
        disassemblyGuideUrl = result.storagePath;
      }

      let popsPfasTestReportDocId =
        !removeExistingPopsPfasDocumentRef.current &&
        form.popsPfasStatus === 'verified_compliant'
          ? existingPopsPfasDocumentIdRef.current
          : null;
      if (
        form.popsPfasStatus === 'verified_compliant' &&
        form.popsPfasTestReportFile
      ) {
        const result = await uploadDocument(
          form.popsPfasTestReportFile,
          organisationId,
          user.id,
        );
        if (!result) throw new Error('POPs/PFAS test report upload failed.');
        popsPfasTestReportDocId = result.documentId;
      }

      let microplasticsReportDocId =
        !removeExistingMicroplasticsDocumentRef.current &&
        form.microplasticsStatus === 'tested'
          ? existingMicroplasticsDocumentIdRef.current
          : null;
      if (
        isTextile &&
        form.microplasticsStatus === 'tested' &&
        form.microplasticsReportFile
      ) {
        const result = await uploadDocument(
          form.microplasticsReportFile,
          organisationId,
          user.id,
        );
        if (!result)
          throw new Error('Microplastics shedding report upload failed.');
        microplasticsReportDocId = result.documentId;
      }

      let rohsSupportingDocumentId =
        !removeExistingRohsSupportingDocumentRef.current &&
        (form.rohsStatus === 'without_exemption' ||
          form.rohsStatus === 'with_exemption')
          ? existingRohsSupportingDocumentIdRef.current
          : null;
      if (
        (form.rohsStatus === 'without_exemption' ||
          form.rohsStatus === 'with_exemption') &&
        form.rohsSupportingDocumentFile
      ) {
        const result = await uploadDocument(
          form.rohsSupportingDocumentFile,
          organisationId,
          user.id,
        );
        if (!result) throw new Error('RoHS supporting document upload failed.');
        rohsSupportingDocumentId = result.documentId;
      }

      let dopDocumentPath =
        form.categoryCode === 'CONSTRUCTION_PRODUCTS' &&
        existingDopDocumentPath &&
        !removeExistingDopDocument
          ? existingDopDocumentPath
          : null;
      if (
        form.categoryCode === 'CONSTRUCTION_PRODUCTS' &&
        form.dopDocumentFile
      ) {
        const result = await uploadDocument(
          form.dopDocumentFile,
          organisationId,
          user.id,
        );
        if (!result) {
          throw new Error('Declaration of Performance upload failed.');
        }
        dopDocumentPath = result.storagePath;
      }

      let demolitionGuideUrl =
        form.categoryCode === 'CONSTRUCTION_PRODUCTS'
          ? form.demolitionGuideUrl.trim() ||
            (existingDemolitionGuidePath && !removeExistingDemolitionGuide
              ? existingDemolitionGuidePath
              : null)
          : null;
      if (
        form.categoryCode === 'CONSTRUCTION_PRODUCTS' &&
        form.demolitionGuideFile
      ) {
        const result = await uploadDocument(
          form.demolitionGuideFile,
          organisationId,
          user.id,
        );
        if (!result) {
          throw new Error('Demolition guide upload failed.');
        }
        demolitionGuideUrl = result.storagePath;
      }

      let declarationOfConformityDocumentId =
        form.categoryCode === 'INDUSTRIAL_MACHINERY' &&
        !removeExistingMachineryDeclarationDocumentRef.current &&
        existingMachineryDeclarationDocumentKindRef.current === 'doc' &&
        !isPartlyCompletedMachinery(form.machineClassification)
          ? existingMachineryDeclarationDocumentIdRef.current
          : null;
      if (
        !isPartlyCompletedMachinery(form.machineClassification) &&
        form.declarationOfConformityFile
      ) {
        const result = await uploadDocument(
          form.declarationOfConformityFile,
          organisationId,
          user.id,
        );
        if (!result) {
          throw new Error('EU Declaration of Conformity upload failed.');
        }
        declarationOfConformityDocumentId = result.documentId;
      }

      let declarationOfIncorporationDocumentId =
        form.categoryCode === 'INDUSTRIAL_MACHINERY' &&
        !removeExistingMachineryDeclarationDocumentRef.current &&
        existingMachineryDeclarationDocumentKindRef.current === 'doi' &&
        isPartlyCompletedMachinery(form.machineClassification)
          ? existingMachineryDeclarationDocumentIdRef.current
          : null;
      if (
        isPartlyCompletedMachinery(form.machineClassification) &&
        form.declarationOfIncorporationFile
      ) {
        const result = await uploadDocument(
          form.declarationOfIncorporationFile,
          organisationId,
          user.id,
        );
        if (!result) {
          throw new Error('EU Declaration of Incorporation upload failed.');
        }
        declarationOfIncorporationDocumentId = result.documentId;
      }

      let notifiedBodyCertificateDocumentId =
        !removeExistingNotifiedBodyCertificateRef.current &&
        form.machineClassification === 'HIGH_RISK_MACHINERY'
          ? existingNotifiedBodyCertificateIdRef.current
          : null;
      if (
        form.machineClassification === 'HIGH_RISK_MACHINERY' &&
        form.notifiedBodyCertificateFile
      ) {
        const result = await uploadDocument(
          form.notifiedBodyCertificateFile,
          organisationId,
          user.id,
        );
        if (!result) {
          throw new Error('EU Type-Examination Certificate upload failed.');
        }
        notifiedBodyCertificateDocumentId = result.documentId;
      }

      const finalProductMetadata = buildProductMetadata(form, {
        declarationOfConformityDocumentId,
        declarationOfIncorporationDocumentId,
        dopDocumentPath,
        demolitionGuideUrl,
        notifiedBodyCertificateDocumentId,
      });
      if (
        JSON.stringify(finalProductMetadata) !== JSON.stringify(productMetadata)
      ) {
        const { error: metadataUpdateError } = await supabase
          .from('products')
          .update({ metadata: finalProductMetadata })
          .eq('id', savedProductId);
        if (metadataUpdateError) throw metadataUpdateError;
      }

      const materials = form.materials
        .filter((material) => material.material_name.trim())
        .map((material) => ({
          product_id: savedProductId,
          material_name: material.material_name.trim(),
          weight_percentage: Number(material.weight_percentage) || 0,
          recycled_content_percentage:
            asOptionalNumber(material.recycled_content_percentage) ?? 0,
          is_recyclable: material.is_recyclable,
        }));

      const packagingItems = form.includeConsumerPackaging
        ? form.packagingItems
            .filter((item) => item.packaging_category && item.material_type)
            .map((item) => ({
              product_id: savedProductId,
              packaging_category:
                item.packaging_category === 'ecommerce'
                  ? 'tertiary'
                  : item.packaging_category,
              material_type: item.material_type,
              weight_grams: asOptionalNumber(item.weight_grams) ?? 0,
              pcr_content_percent:
                asOptionalNumber(item.pcr_content_percent) ?? 0,
              recyclability_status: item.recyclability_status || null,
              epr_registration_number:
                form.packagingEprRegistration.trim() || null,
            }))
        : [];

      const reachDocumentIds = form.documentIds.filter(Boolean);
      const svhcRows = form.svhcDeclarations.filter((declaration) =>
        declaration.substance_name.trim(),
      );
      const reachDocumentCount =
        form.svhcStatus === 'contains'
          ? svhcRows.length
          : Math.min(reachDocumentIds.length, 1);
      const rohsDocumentId = rohsSupportingDocumentId;
      const genericCeDocumentIds = reachDocumentIds.slice(reachDocumentCount);
      const ceDocumentIds = Array.from(
        new Set(
          [
            ...genericCeDocumentIds,
            declarationOfConformityDocumentId,
            declarationOfIncorporationDocumentId,
            notifiedBodyCertificateDocumentId,
          ].filter((value): value is string => Boolean(value)),
        ),
      );

      const declarations = [
        ...(form.svhcStatus === 'contains'
          ? svhcRows.map((declaration, index) => ({
              product_id: savedProductId,
              declaration_type: 'REACH_SVHC',
              status: 'UNDER_REVIEW',
              svhc_present: true,
              substance_name: declaration.substance_name.trim(),
              cas_ec_number: declaration.cas_ec_number.trim() || null,
              present_in_component:
                declaration.present_in_component.trim() || null,
              scip_number: declaration.scip_dossier_number.trim() || null,
              document_id: reachDocumentIds[index] ?? null,
            }))
          : form.svhcStatus === 'none'
            ? [
                {
                  product_id: savedProductId,
                  declaration_type: 'REACH_SVHC',
                  status: 'COMPLIANT',
                  svhc_present: false,
                  substance_name: null,
                  cas_ec_number: null,
                  present_in_component: null,
                  scip_number: null,
                  document_id: reachDocumentIds[0] ?? null,
                },
              ]
            : []),
        ...(form.rohsStatus
          ? [
              {
                product_id: savedProductId,
                declaration_type: 'ROHS',
                status:
                  form.rohsStatus === 'not_applicable' ? 'EXEMPT' : 'COMPLIANT',
                svhc_present: null,
                substance_name: null,
                cas_ec_number: null,
                present_in_component: null,
                scip_number: null,
                document_id: rohsDocumentId,
              },
            ]
          : []),
        ...((isTextile || isBattery) && form.popsPfasStatus
          ? [
              {
                product_id: savedProductId,
                declaration_type: 'POPS_PFAS',
                status:
                  form.popsPfasStatus === 'verified_compliant'
                    ? 'COMPLIANT'
                    : 'UNDER_REVIEW',
                svhc_present: form.popsPfasStatus === 'contains_restricted',
                substance_name: null,
                cas_ec_number: null,
                present_in_component:
                  form.popsPfasStatus === 'contains_restricted'
                    ? form.popsPfasExemptionDetails.trim() || null
                    : null,
                scip_number: null,
                document_id:
                  form.popsPfasStatus === 'verified_compliant'
                    ? popsPfasTestReportDocId
                    : null,
              },
            ]
          : []),
        ...ceDocumentIds.map((documentId) => ({
          product_id: savedProductId,
          declaration_type: 'CE_DOC',
          status: 'UNDER_REVIEW',
          svhc_present: null,
          substance_name: null,
          cas_ec_number: null,
          present_in_component: null,
          scip_number: null,
          document_id: documentId,
        })),
        ...(isTextile && microplasticsReportDocId
          ? [
              {
                product_id: savedProductId,
                declaration_type: 'MICROPLASTICS',
                status: 'UNDER_REVIEW',
                svhc_present: null,
                substance_name: null,
                cas_ec_number: null,
                present_in_component: null,
                scip_number: null,
                document_id: microplasticsReportDocId,
              },
            ]
          : []),
      ];

      if (wasEditingExistingProduct) {
        const deleteResults = await Promise.all([
          supabase
            .from('product_materials')
            .delete()
            .eq('product_id', savedProductId),
          supabase
            .from('product_packaging')
            .delete()
            .eq('product_id', savedProductId),
          supabase
            .from('product_declarations')
            .delete()
            .eq('product_id', savedProductId),
        ]);

        const deleteError = deleteResults.find((result) => result.error)?.error;
        if (deleteError) throw deleteError;
      }

      if (materials.length > 0) {
        const { error: materialsError } = await supabase
          .from('product_materials')
          .insert(materials);
        if (materialsError) throw materialsError;
      }

      if (packagingItems.length > 0) {
        const { error: packagingError } = await supabase
          .from('product_packaging')
          .insert(packagingItems);
        if (packagingError) throw packagingError;
      }

      if (declarations.length > 0) {
        const { error: declarationsError } = await supabase
          .from('product_declarations')
          .insert(declarations);
        if (declarationsError) throw declarationsError;
      }

      const { error: circularityError } = await supabase
        .from('product_circularity')
        .upsert(
          {
            product_id: savedProductId,
            carbon_footprint_kg_co2e: asOptionalNumber(form.carbonFootprint),
            expected_lifetime_years:
              form.lifespanUnit === 'months'
                ? (asOptionalNumber(form.lifespan) ?? 0) / 12
                : asOptionalNumber(form.lifespan),
            repairability_score: asOptionalNumber(form.repairabilityScore),
            repair_manual_url: repairManualUrl,
            disassembly_guide_url: disassemblyGuideUrl,
            recycling_instructions: form.recyclingInstructions.trim() || null,
            rated_capacity_ah: isBattery
              ? asOptionalNumber(form.ratedCapacityAh)
              : null,
            rated_energy_kwh: isBattery
              ? asOptionalNumber(form.ratedEnergyKwh)
              : null,
            nominal_voltage_v: isBattery
              ? asOptionalNumber(form.nominalVoltageV)
              : null,
            minimum_voltage_v: isBattery
              ? asOptionalNumber(form.minimumVoltageV)
              : null,
            maximum_voltage_v: isBattery
              ? asOptionalNumber(form.maximumVoltageV)
              : null,
            power_capability_limits: isBattery
              ? form.powerCapabilityLimits.trim() || null
              : null,
            round_trip_efficiency_percent: isBattery
              ? asOptionalNumber(form.roundTripEfficiencyPercent)
              : null,
            cycle_lifetime_descriptor: isBattery
              ? form.cycleLifetimeDescriptor.trim() || null
              : null,
            operating_temperature_window: isBattery
              ? form.operatingTemperatureWindow.trim() || null
              : null,
            extinguishing_agents: isBattery
              ? form.extinguishingAgents.trim() || null
              : null,
            safety_handling_measures: isBattery
              ? form.safetyHandlingMeasures.trim() || null
              : null,
            ...(isTextile
              ? {
                  water_consumption_liters: asOptionalNumber(
                    form.waterConsumption,
                  ),
                  color_fastness_rating: asOptionalNumber(
                    form.colorFastnessRating,
                  ),
                  pilling_resistance_rating: asOptionalNumber(
                    form.pillingResistanceRating,
                  ),
                  spare_parts_availability:
                    form.sparePartsAvailability.trim() || null,
                  spare_parts_availability_years: null,
                  spare_parts_catalog_url: null,
                  take_back_scheme: form.takeBackScheme.trim() || null,
                  microplastics_status: form.microplasticsStatus || null,
                  epr_registration_number:
                    form.eprRegistrationNumber.trim() || null,
                  care_symbols:
                    form.careSymbols.length > 0 ? form.careSymbols : null,
                  software_support_years: null,
                }
              : {
                  water_consumption_liters: null,
                  color_fastness_rating: null,
                  pilling_resistance_rating: null,
                  spare_parts_availability: null,
                  spare_parts_availability_years: isIndustrial
                    ? asOptionalNumber(form.sparePartsAvailabilityYears)
                    : null,
                  spare_parts_catalog_url: isIndustrial
                    ? form.sparePartsCatalogUrl.trim() || null
                    : null,
                  take_back_scheme: null,
                  microplastics_status: null,
                  epr_registration_number: null,
                  care_symbols: null,
                  software_support_years: isIndustrial
                    ? asOptionalNumber(form.softwareSupportYears)
                    : null,
                }),
          },
          { onConflict: 'product_id' },
        );
      if (circularityError) throw circularityError;

      if (wasEditingExistingProduct) {
        const { error: passportUpdateError } = await supabase
          .from('product_passports')
          .update({
            visibility_settings: {
              technical_specs_access: form.restrictTechnicalSpecs
                ? 'restricted'
                : 'public',
            },
          })
          .eq('product_id', savedProductId);
        if (passportUpdateError) throw passportUpdateError;
      } else {
        const { error: passportError } = await supabase
          .from('product_passports')
          .insert({
            product_id: savedProductId,
            granularity_level: 'MODEL',
            status: 'DRAFT',
            visibility_settings: {
              technical_specs_access: form.restrictTechnicalSpecs
                ? 'restricted'
                : 'public',
            },
          });
        if (passportError) throw passportError;
        setProductId(savedProductId);
        setExistingPassport({
          status: 'DRAFT',
          published_at: null,
          visibility_settings: {
            technical_specs_access: form.restrictTechnicalSpecs
              ? 'restricted'
              : 'public',
          },
        });
      }

      setHasUnsavedChanges(false);

      if (nextStepAfterSave) {
        setStep(nextStepAfterSave);
      }

      if (exitAfterSave) {
        onCreated(savedProductId);
      }

      return savedProductId;
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save product draft.',
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    const stepValidationErrors = validateStep(step);
    if (Object.keys(stepValidationErrors).length > 0) {
      setValidationErrors(stepValidationErrors);
      setError(null);
      return;
    }

    setValidationErrors({});
    setError(null);

    if (step === 1 && !isEditMode) {
      await persistDraft({ exitAfterSave: false, nextStepAfterSave: 2 });
      return;
    }

    setStep((currentStep) => Math.min(currentStep + 1, 3) as Step);
  };

  const handleSaveDraft = async () => {
    const identityErrors = validateStep(1);
    const complianceErrors = validateStep(2);
    const circularityErrors = validateStep(3);
    const firstInvalidStep = Object.keys(identityErrors).length
      ? 1
      : Object.keys(complianceErrors).length
        ? 2
        : Object.keys(circularityErrors).length
          ? 3
          : null;

    if (firstInvalidStep) {
      const firstStepErrors =
        firstInvalidStep === 1
          ? identityErrors
          : firstInvalidStep === 2
            ? complianceErrors
            : circularityErrors;

      setValidationErrors(firstStepErrors);
      setError(null);
      setStep(firstInvalidStep);
      return;
    }
    await persistDraft({ exitAfterSave: true });
  };

  const handleSaveDraftAndExit = async () => {
    await persistDraft({ exitAfterSave: true });
  };

  const handleCancel = () => {
    if (saving || uploading) return;

    if (
      isEditMode &&
      hasUnsavedChanges &&
      !window.confirm(
        'There are unsaved changes, are you sure you would like to exit and discard them?',
      )
    ) {
      return;
    }

    onCancel();
  };

  const stepLabels = [
    'Identity & Classification',
    'Materials & Compliance',
    'Circularity & Preview',
  ];

  return (
    <Container fluid className="px-0">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            onClick={handleCancel}
            disabled={saving || uploading}
          >
            <i className="bi bi-arrow-left me-2" />
            Products
          </Button>
          <h2 className="mt-2 mb-1">
            {isEditMode ? 'Edit Product' : 'Add New Product'}
          </h2>
          <div className="text-muted">
            {isEditMode
              ? isPublishedPassport
                ? 'Update the live Digital Product Passport for this product.'
                : 'Update the existing Digital Product Passport draft for this product.'
              : 'Create a draft Digital Product Passport for a product in your catalog.'}
          </div>
        </div>
        <div className="text-muted small text-lg-end">
          Step {step} of {stepLabels.length}
        </div>
      </div>

      <div className="border rounded bg-white overflow-hidden">
        <div className="border-bottom bg-light p-3 p-lg-4">
          <div className="d-flex flex-wrap gap-2 small">
            {stepLabels.map((label, index) => {
              const stepNumber = index + 1;
              return (
                <span
                  key={label}
                  className={
                    stepNumber === step
                      ? 'fw-semibold text-primary'
                      : stepNumber < step
                        ? 'text-success'
                        : 'text-muted'
                  }
                >
                  {stepNumber}. {label}
                  {stepNumber < 3 ? '  >' : ''}
                </span>
              );
            })}
          </div>
        </div>
        <div className="p-3 p-lg-4" style={{ minHeight: '560px' }}>
          {error && <Alert variant="danger">{error}</Alert>}
          {uploadError && <Alert variant="danger">{uploadError}</Alert>}
          {isEditMode && isPublishedPassport && (
            <Alert variant="warning">
              This passport is already published. Any saved edits here will
              update the live public DPP immediately.
            </Alert>
          )}
          {loadingExistingProduct && (
            <Spinner animation="border" size="sm" className="mb-3" />
          )}

          {step === 1 && (
            <AddProductStepIdentity
              form={form}
              skuExistsError={skuExistsError}
              skuCheckPending={skuCheckPending}
              gtinValidation={gtinValidation}
              gtinExistsError={gtinExistsError}
              gtinCheckPending={gtinCheckPending}
              isTextile={isTextile}
              isElectronics={isElectronics}
              isIndustrial={isIndustrial}
              validationErrors={validationErrors}
              categoryOptions={categoryOptions}
              updateForm={updateForm}
              changeCategory={changeCategory}
              existingMachineryDeclarationDocumentName={
                existingMachineryDeclarationDocumentName
              }
              existingMachineryDeclarationDocumentSignedUrl={
                existingMachineryDeclarationDocumentSignedUrl
              }
              removeExistingMachineryDeclarationDocument={
                removeExistingMachineryDeclarationDocument
              }
              onRemoveExistingMachineryDeclarationDocument={() => {
                setExistingMachineryDeclarationDocumentId(null);
                existingMachineryDeclarationDocumentIdRef.current = null;
                setExistingMachineryDeclarationDocumentName(null);
                setExistingMachineryDeclarationDocumentPath(null);
                setExistingMachineryDeclarationDocumentSignedUrl(null);
                setExistingMachineryDeclarationDocumentKind(null);
                existingMachineryDeclarationDocumentKindRef.current = null;
                setRemoveExistingMachineryDeclarationDocument(true);
                removeExistingMachineryDeclarationDocumentRef.current = true;
                updateForm('declarationOfConformityFile', null);
                updateForm('declarationOfIncorporationFile', null);
              }}
              onMachineryDeclarationFileChange={(file) => {
                const declarationField = getMachineryDeclarationField(
                  form.machineClassification,
                );
                const nextKind = isPartlyCompletedMachinery(
                  form.machineClassification,
                )
                  ? 'doi'
                  : 'doc';

                if (!file) {
                  updateForm('declarationOfConformityFile', null);
                  updateForm('declarationOfIncorporationFile', null);
                  return;
                }

                if (!isPdfFile(file)) {
                  setValidationErrors((current) => ({
                    ...current,
                    [declarationField]:
                      nextKind === 'doi'
                        ? 'Upload the EU Declaration of Incorporation as a PDF.'
                        : 'Upload the EU Declaration of Conformity as a PDF.',
                  }));
                  return;
                }

                if (file.size > MACHINERY_DECLARATION_MAX_BYTES) {
                  setValidationErrors((current) => ({
                    ...current,
                    [declarationField]:
                      nextKind === 'doi'
                        ? 'EU Declaration of Incorporation PDF must be 10MB or less.'
                        : 'EU Declaration of Conformity PDF must be 10MB or less.',
                  }));
                  return;
                }

                if (isEditMode) {
                  void (async () => {
                    setError(null);
                    const result =
                      await uploadMachineryDeclarationForExistingProduct(file);
                    if (!result) return;

                    setExistingMachineryDeclarationDocumentId(
                      result.documentId,
                    );
                    existingMachineryDeclarationDocumentIdRef.current =
                      result.documentId;
                    setExistingMachineryDeclarationDocumentName(file.name);
                    setExistingMachineryDeclarationDocumentPath(
                      result.storagePath,
                    );
                    setExistingMachineryDeclarationDocumentSignedUrl(null);
                    setExistingMachineryDeclarationDocumentKind(nextKind);
                    existingMachineryDeclarationDocumentKindRef.current =
                      nextKind;
                    setRemoveExistingMachineryDeclarationDocument(false);
                    removeExistingMachineryDeclarationDocumentRef.current = false;
                    updateForm('declarationOfConformityFile', null);
                    updateForm('declarationOfIncorporationFile', null);
                  })();
                  return;
                }

                setRemoveExistingMachineryDeclarationDocument(false);
                removeExistingMachineryDeclarationDocumentRef.current = false;
                setExistingMachineryDeclarationDocumentKind(nextKind);
                existingMachineryDeclarationDocumentKindRef.current = nextKind;
                updateForm(
                  'declarationOfConformityFile',
                  declarationField === 'declarationOfConformityFile'
                    ? file
                    : null,
                );
                updateForm(
                  'declarationOfIncorporationFile',
                  declarationField === 'declarationOfIncorporationFile'
                    ? file
                    : null,
                );
              }}
              existingNotifiedBodyCertificateName={
                existingNotifiedBodyCertificateName
              }
              existingNotifiedBodyCertificateSignedUrl={
                existingNotifiedBodyCertificateSignedUrl
              }
              removeExistingNotifiedBodyCertificate={
                removeExistingNotifiedBodyCertificate
              }
              onRemoveExistingNotifiedBodyCertificate={() => {
                setExistingNotifiedBodyCertificateId(null);
                existingNotifiedBodyCertificateIdRef.current = null;
                setExistingNotifiedBodyCertificateName(null);
                setExistingNotifiedBodyCertificatePath(null);
                setExistingNotifiedBodyCertificateSignedUrl(null);
                setRemoveExistingNotifiedBodyCertificate(true);
                removeExistingNotifiedBodyCertificateRef.current = true;
                updateForm('notifiedBodyCertificateFile', null);
              }}
              onNotifiedBodyCertificateFileChange={(file) => {
                if (!file) {
                  updateForm('notifiedBodyCertificateFile', null);
                  return;
                }

                if (!isPdfFile(file)) {
                  setValidationErrors((current) => ({
                    ...current,
                    notifiedBodyCertificateFile:
                      'Upload the EU Type-Examination Certificate as a PDF.',
                  }));
                  return;
                }

                if (isEditMode) {
                  void (async () => {
                    setError(null);
                    const result =
                      await uploadNotifiedBodyCertificateForExistingProduct(
                        file,
                      );
                    if (!result) return;

                    setExistingNotifiedBodyCertificateId(result.documentId);
                    existingNotifiedBodyCertificateIdRef.current =
                      result.documentId;
                    setExistingNotifiedBodyCertificateName(file.name);
                    setExistingNotifiedBodyCertificatePath(result.storagePath);
                    setExistingNotifiedBodyCertificateSignedUrl(null);
                    setRemoveExistingNotifiedBodyCertificate(false);
                    removeExistingNotifiedBodyCertificateRef.current = false;
                    updateForm('notifiedBodyCertificateFile', null);
                  })();
                  return;
                }

                setRemoveExistingNotifiedBodyCertificate(false);
                removeExistingNotifiedBodyCertificateRef.current = false;
                updateForm('notifiedBodyCertificateFile', file);
              }}
            />
          )}

          {step === 2 && (
            <AddProductStepCompliance
              form={form}
              isTextile={isTextile}
              isElectronics={isElectronics}
              bomTotal={bomTotal}
              validationErrors={validationErrors}
              echaCandidateSubstances={echaCandidateSubstances}
              existingDopDocumentPath={existingDopDocumentPath}
              existingDopDocumentSignedUrl={existingDopDocumentSignedUrl}
              removeExistingDopDocument={removeExistingDopDocument}
              existingRohsSupportingDocumentName={
                existingRohsSupportingDocumentName
              }
              existingRohsSupportingDocumentSignedUrl={
                existingRohsSupportingDocumentSignedUrl
              }
              removeExistingRohsSupportingDocument={
                removeExistingRohsSupportingDocument
              }
              existingPopsPfasDocumentName={existingPopsPfasDocumentName}
              existingPopsPfasDocumentSignedUrl={
                existingPopsPfasDocumentSignedUrl
              }
              removeExistingPopsPfasDocument={removeExistingPopsPfasDocument}
              updateForm={updateForm}
              updateMaterial={updateMaterial}
              updatePackagingItem={updatePackagingItem}
              updateSupplyChainSupplier={updateSupplyChainSupplier}
              updateSvhcDeclaration={updateSvhcDeclaration}
              addPackagingItem={addPackagingItem}
              addSupplyChainSupplier={addSupplyChainSupplier}
              addSvhcDeclaration={addSvhcDeclaration}
              handleSvhcSubstanceChange={handleSvhcSubstanceChange}
              onRemoveExistingDopDocument={() => {
                setExistingDopDocumentPath(null);
                setExistingDopDocumentSignedUrl(null);
                setRemoveExistingDopDocument(true);
                updateForm('dopDocumentFile', null);
              }}
              onDopDocumentFileChange={(file) => {
                if (!file) {
                  updateForm('dopDocumentFile', null);
                  return;
                }

                setRemoveExistingDopDocument(false);
                updateForm('dopDocumentFile', file);
              }}
              onRemoveExistingRohsSupportingDocument={() => {
                setExistingRohsSupportingDocumentId(null);
                existingRohsSupportingDocumentIdRef.current = null;
                setExistingRohsSupportingDocumentName(null);
                setExistingRohsSupportingDocumentPath(null);
                setExistingRohsSupportingDocumentSignedUrl(null);
                setRemoveExistingRohsSupportingDocument(true);
                removeExistingRohsSupportingDocumentRef.current = true;
                updateForm('rohsSupportingDocumentFile', null);
              }}
              onRohsSupportingDocumentFileChange={(file) => {
                if (!file) {
                  updateForm('rohsSupportingDocumentFile', null);
                  return;
                }

                if (isEditMode) {
                  void (async () => {
                    setError(null);
                    const result =
                      await uploadRohsDocumentForExistingProduct(file);
                    if (!result) return;

                    setExistingRohsSupportingDocumentId(result.documentId);
                    existingRohsSupportingDocumentIdRef.current =
                      result.documentId;
                    setExistingRohsSupportingDocumentName(file.name);
                    setExistingRohsSupportingDocumentPath(result.storagePath);
                    setExistingRohsSupportingDocumentSignedUrl(null);
                    setRemoveExistingRohsSupportingDocument(false);
                    removeExistingRohsSupportingDocumentRef.current = false;
                    updateForm('rohsSupportingDocumentFile', null);
                  })();
                  return;
                }

                setRemoveExistingRohsSupportingDocument(false);
                removeExistingRohsSupportingDocumentRef.current = false;
                updateForm('rohsSupportingDocumentFile', file);
              }}
              onRemoveExistingPopsPfasDocument={() => {
                setExistingPopsPfasDocumentId(null);
                existingPopsPfasDocumentIdRef.current = null;
                setExistingPopsPfasDocumentName(null);
                setExistingPopsPfasDocumentPath(null);
                setExistingPopsPfasDocumentSignedUrl(null);
                setRemoveExistingPopsPfasDocument(true);
                removeExistingPopsPfasDocumentRef.current = true;
                updateForm('popsPfasTestReportFile', null);
              }}
              onPopsPfasTestReportFileChange={(file) => {
                if (!file) {
                  updateForm('popsPfasTestReportFile', null);
                  return;
                }

                if (isEditMode) {
                  void (async () => {
                    setError(null);
                    const result =
                      await uploadPopsPfasDocumentForExistingProduct(file);
                    if (!result) return;

                    setExistingPopsPfasDocumentId(result.documentId);
                    existingPopsPfasDocumentIdRef.current = result.documentId;
                    setExistingPopsPfasDocumentName(file.name);
                    setExistingPopsPfasDocumentPath(result.storagePath);
                    setExistingPopsPfasDocumentSignedUrl(null);
                    setRemoveExistingPopsPfasDocument(false);
                    removeExistingPopsPfasDocumentRef.current = false;
                    updateForm('popsPfasTestReportFile', null);
                  })();
                  return;
                }

                setRemoveExistingPopsPfasDocument(false);
                removeExistingPopsPfasDocumentRef.current = false;
                updateForm('popsPfasTestReportFile', file);
              }}
            />
          )}

          {step === 3 && (
            <AddProductStepCircularity
              form={form}
              isTextile={isTextile}
              isElectronics={isElectronics}
              isIndustrial={isIndustrial}
              categoryName={categoryName ?? ''}
              completeness={completeness}
              completenessChecks={completenessChecks}
              validationErrors={validationErrors}
              careSymbolCategories={careSymbolCategories}
              existingRepairManualPath={existingRepairManualPath}
              existingRepairManualSignedUrl={existingRepairManualSignedUrl}
              removeExistingRepairManual={removeExistingRepairManual}
              existingDisassemblyGuidePath={existingDisassemblyGuidePath}
              existingDisassemblyGuideSignedUrl={
                existingDisassemblyGuideSignedUrl
              }
              removeExistingDisassemblyGuide={removeExistingDisassemblyGuide}
              existingDemolitionGuidePath={existingDemolitionGuidePath}
              existingDemolitionGuideSignedUrl={
                existingDemolitionGuideSignedUrl
              }
              removeExistingDemolitionGuide={removeExistingDemolitionGuide}
              existingMicroplasticsDocumentName={
                existingMicroplasticsDocumentName
              }
              existingMicroplasticsDocumentSignedUrl={
                existingMicroplasticsDocumentSignedUrl
              }
              removeExistingMicroplasticsDocument={
                removeExistingMicroplasticsDocument
              }
              updateForm={updateForm}
              onRemoveExistingRepairManual={async () => {
                const removed =
                  await removeGuideFromExistingProduct('repair_manual_url');
                if (!removed) return;
                setExistingRepairManualPath(null);
                setExistingRepairManualSignedUrl(null);
                setRemoveExistingRepairManual(true);
                updateForm('repairManualFile', null);
              }}
              onRepairManualFileChange={async (file) => {
                if (!file) {
                  updateForm('repairManualFile', null);
                  return;
                }
                if (isEditMode) {
                  const storagePath = await uploadGuideForExistingProduct(
                    file,
                    'repair_manual_url',
                  );
                  if (!storagePath) return;
                  setExistingRepairManualPath(storagePath);
                  setRemoveExistingRepairManual(false);
                  updateForm('repairManualFile', null);
                  return;
                }
                setRemoveExistingRepairManual(false);
                updateForm('repairManualFile', file);
              }}
              onRemoveExistingDisassemblyGuide={async () => {
                const removed = await removeGuideFromExistingProduct(
                  'disassembly_guide_url',
                );
                if (!removed) return;
                setExistingDisassemblyGuidePath(null);
                setExistingDisassemblyGuideSignedUrl(null);
                setRemoveExistingDisassemblyGuide(true);
                updateForm('disassemblyGuideFile', null);
              }}
              onDisassemblyGuideFileChange={async (file) => {
                if (!file) {
                  updateForm('disassemblyGuideFile', null);
                  return;
                }
                if (isEditMode) {
                  const storagePath = await uploadGuideForExistingProduct(
                    file,
                    'disassembly_guide_url',
                  );
                  if (!storagePath) return;
                  setExistingDisassemblyGuidePath(storagePath);
                  setRemoveExistingDisassemblyGuide(false);
                  updateForm('disassemblyGuideFile', null);
                  return;
                }
                setRemoveExistingDisassemblyGuide(false);
                updateForm('disassemblyGuideFile', file);
              }}
              onRemoveExistingDemolitionGuide={() => {
                setExistingDemolitionGuidePath(null);
                setExistingDemolitionGuideSignedUrl(null);
                setRemoveExistingDemolitionGuide(true);
                updateForm('demolitionGuideFile', null);
              }}
              onDemolitionGuideFileChange={(file) => {
                if (!file) {
                  updateForm('demolitionGuideFile', null);
                  return;
                }

                setRemoveExistingDemolitionGuide(false);
                updateForm('demolitionGuideFile', file);
              }}
              onRemoveExistingMicroplasticsDocument={() => {
                existingMicroplasticsDocumentIdRef.current = null;
                setExistingMicroplasticsDocumentName(null);
                setExistingMicroplasticsDocumentPath(null);
                setExistingMicroplasticsDocumentSignedUrl(null);
                setRemoveExistingMicroplasticsDocument(true);
                removeExistingMicroplasticsDocumentRef.current = true;
                updateForm('microplasticsReportFile', null);
              }}
              onMicroplasticsReportFileChange={(file) => {
                if (!file) {
                  updateForm('microplasticsReportFile', null);
                  return;
                }

                if (isEditMode) {
                  void (async () => {
                    setError(null);
                    const result =
                      await uploadMicroplasticsDocumentForExistingProduct(file);
                    if (!result) return;

                    existingMicroplasticsDocumentIdRef.current =
                      result.documentId;
                    setExistingMicroplasticsDocumentName(file.name);
                    setExistingMicroplasticsDocumentPath(result.storagePath);
                    setExistingMicroplasticsDocumentSignedUrl(null);
                    setRemoveExistingMicroplasticsDocument(false);
                    removeExistingMicroplasticsDocumentRef.current = false;
                    updateForm('microplasticsReportFile', null);
                  })();
                  return;
                }

                setRemoveExistingMicroplasticsDocument(false);
                removeExistingMicroplasticsDocumentRef.current = false;
                updateForm('microplasticsReportFile', file);
              }}
            />
          )}
        </div>
        <div className="border-top bg-white p-3 p-lg-4 d-flex flex-wrap justify-content-between align-items-center gap-3 sticky-bottom">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={handleCancel}
              disabled={saving || uploading}
            >
              Cancel
            </Button>
            {isEditMode && !isPublishedPassport && (
              <Button
                variant="outline-primary"
                onClick={handleSaveDraftAndExit}
                disabled={saving || uploading}
              >
                Save draft & exit
              </Button>
            )}
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-3">
            {footerValidationMessage && (
              <span className="small text-danger">
                {footerValidationMessage}
              </span>
            )}
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setValidationErrors({});
                  setError(null);
                  setStep(
                    (currentStep) => Math.max(currentStep - 1, 1) as Step,
                  );
                }}
                disabled={step === 1 || saving || uploading}
              >
                Back
              </Button>
              {step < 3 ? (
                <Button
                  variant="primary"
                  onClick={handleContinue}
                  disabled={saving || uploading}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSaveDraft}
                  disabled={saving || uploading}
                >
                  {saving || uploading ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Saving...
                    </>
                  ) : isEditMode ? (
                    'Save Changes'
                  ) : (
                    'Save Draft'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
}
